import {
  ClockCircleOutlined,
  DeleteOutlined,
  FileTextOutlined,
  GlobalOutlined,
  HistoryOutlined,
  UploadOutlined,
  UserOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  message,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
  Upload,
} from 'antd';
import * as Component from 'components';
import { globalSettingService } from 'content/global-settings/services/globalSettingService';
import { LanguageInterface } from 'content/global-settings/types/globalSettingType';
import { contentAssetService } from 'content/library/service/contentAssetService';
import { StreamManifest, VideoAssetSet } from 'content/library/types/contentVideoType';
import { seasonContentService } from 'content/season/services/seasonContentService';
import { seasonService } from 'content/service/seasonService';
import { seriesService } from 'content/service/seriesService';
import { SeriesInterface } from 'content/type/seriesType';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { videoService } from 'service/api/videoService';
import { actionHistoryService } from 'src/domain/account/action-history/service/actionHistoryService';
import { ActionHistoryType } from 'src/domain/account/action-history/types/actionHistoryType';
import { videoSubtitleService } from 'src/domain/video/assets/services/videoSubtitleService';
import { SeasonData } from 'type/seasonType';
import { slugify } from 'utils/slugify';
import {
  createDefaultDistributions,
  getApiServiceName,
  SERVICE_OPTIONS,
  SUBTITLE_LANGUAGES,
} from '../../../../constants/serviceDistribution';
import { I18nService } from '../../../../shared/services/i18n/i18nService';
import {
  getPlayableVideoUrl,
  hasPlayableVideoUrl,
  VideoType,
} from '../../../../shared/type/videoType';
import { I18nFieldData } from '../../../../shared/types/i18n/i18nTypes';
import { clearActionSessionId, setActionSessionId } from '../../../../shared/utils/actionSession';
import { formatDuration } from '../../../../shared/utils/timeFormat';
import Preview from '../../../video/assets/components/preview';
import { episodeService } from '../services/episodeService';
import { thumbnailOverrideService } from '../services/thumbnailOverrideService';
import { EpisodeInterface } from '../types/episodeType';
import {
  ContentThumbnailOverride,
  THUMBNAIL_DISTRIBUTION_RULES,
  THUMBNAIL_OVERRIDE_LANGUAGES,
} from '../types/thumbnailOverrideType';
import './EpisodeEdit.css';

import './EpisodeEdit.css';

const { Text } = Typography;

const ManifestSubtitleTags: React.FC<{ url: string }> = ({ url }) => {
  const [languages, setLanguages] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;

    if (!url || !url.includes('.m3u8')) {
      setLoading(false);
      console.log('No URL');
      return;
    }

    const fetchManifest = async () => {
      try {
        setLoading(true);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const text = await response.text();
        console.log('Manifest text:', text);

        // Parse lines to find TYPE=SUBTITLES and extract LANGUAGE
        const lines = text.split('\n');
        const subtitleLines = lines.filter(
          line => line.startsWith('#EXT-X-MEDIA:') && line.includes('TYPE=SUBTITLES')
        );

        const foundLangs = subtitleLines
          .map(line => {
            const match = line.match(/LANGUAGE="([^"]+)"/);
            return match ? match[1] : null;
          })
          .filter(Boolean) as string[];

        const uniqueLangs = Array.from(new Set(foundLangs));
        console.log('isMounted', isMounted);
        console.log('uniqueLangs', uniqueLangs);

        if (isMounted) {
          setLanguages(uniqueLangs);
        }
      } catch (error) {
        console.error('Failed to fetch or parse manifest for subtitles:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchManifest();

    return () => {
      isMounted = false;
    };
  }, [url]);

  if (loading) {
    return <Spin size="small" style={{ marginLeft: '8px' }} />;
  }

  if (languages.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '8px', textAlign: 'center' }}>
      {languages.map(lang => (
        <Tag key={lang} color="blue" style={{ fontSize: '10px', marginRight: '4px' }}>
          {lang}
        </Tag>
      ))}
    </div>
  );
};

const EpisodeEdit: React.FC = () => {
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [episode, setEpisode] = useState<EpisodeInterface | null>(null);
  const [writeLanguages, setWriteLanguages] = useState<LanguageInterface[]>([]);

  // Season & Series 정보
  const [season, setSeason] = useState<SeasonData | null>(null);
  const [series, setSeries] = useState<SeriesInterface | null>(null);
  const [seasonId] = useState<number | undefined>(
    searchParams.get('season_id') && searchParams.get('season_id') !== 'pending'
      ? Number(searchParams.get('season_id'))
      : undefined
  );
  const [isPendingSeason] = useState<boolean>(searchParams.get('season_id') === 'pending');
  const [seriesId] = useState<number | undefined>(
    searchParams.get('series_id') ? Number(searchParams.get('series_id')) : undefined
  );

  // i18n 필드 상태
  const [i18nTitle, setI18nTitle] = useState<I18nFieldData>({});
  const [i18nSecondaryTitle, setI18nSecondaryTitle] = useState<I18nFieldData>({});
  const [i18nSeoTitle, setI18nSeoTitle] = useState<I18nFieldData>({});
  const [i18nSeoSynopsis, setI18nSeoSynopsis] = useState<I18nFieldData>({});
  const [i18nSeoKeyword, setI18nSeoKeyword] = useState<I18nFieldData>({});

  // Video Asset search
  const [videoAssetKeyword, setVideoAssetKeyword] = useState<string>('');
  const [videoAssets, setVideoAssets] = useState<VideoType[]>([]);
  const [selectedVideoAssets, setSelectedVideoAssets] = useState<number[]>([]);
  const [isLoadingVideoAssets, setIsLoadingVideoAssets] = useState(false);
  const [videoAssetCurrentPage, setVideoAssetCurrentPage] = useState<number>(1);
  const [videoAssetTotal, setVideoAssetTotal] = useState<number>(0);
  const [linkedVideoAsset, setLinkedVideoAsset] = useState<VideoType | null>(null);
  const [linkedVideoAssetSubtitles, setLinkedVideoAssetSubtitles] = useState<any[]>([]);
  const [existingContentVideoAssets, setExistingContentVideoAssets] = useState<VideoAssetSet[]>([]);
  const [linkedVideoAssetsData, setLinkedVideoAssetsData] = useState<
    (VideoAssetSet & { videoData?: VideoType })[]
  >([]);

  // Service Auto Distribution
  const [inherit, setInherit] = useState(true); // true: 상속받기, false: 자체 설정
  const [distributions, setDistributions] = useState<any[]>([]);

  // Thumbnail Override
  const [thumbnailOverrides, setThumbnailOverrides] = useState<
    Map<string, ContentThumbnailOverride>
  >(new Map());
  const [uploadedThumbnails, setUploadedThumbnails] = useState<Map<string, string>>(new Map());
  const [savingThumbnails, setSavingThumbnails] = useState(false);

  // Action History 모달 관련 상태
  const [actionHistoryModalVisible, setActionHistoryModalVisible] = useState(false);
  const [actionHistories, setActionHistories] = useState<ActionHistoryType[]>([]);
  const [actionHistoryLoading, setActionHistoryLoading] = useState(false);

  // Season 검색 모달 관련 상태
  const [seasonSearchModalVisible, setSeasonSearchModalVisible] = useState(false);
  const [searchSeasons, setSearchSeasons] = useState<SeasonData[]>([]);
  const [seasonSearchKeyword, setSeasonSearchKeyword] = useState<string>('');
  const [seasonSearchLoading, setSeasonSearchLoading] = useState(false);
  const [defaultSeasons, setDefaultSeasons] = useState<SeasonData[]>([]);

  const initializeDefaultDistributions = () => {
    setDistributions(createDefaultDistributions());
  };

  const updateDistribution = (serviceName: string, environment: string, updates: any) => {
    setDistributions(prev =>
      prev.map(dist =>
        dist.service_name === serviceName && dist.environment === environment
          ? { ...dist, ...updates }
          : dist
      )
    );
  };

  // Series의 distribution을 포맷팅하는 함수
  const formatSeriesDistributions = useMemo(() => {
    if (!series || !(series as any).service_auto_distributions) {
      return createDefaultDistributions();
    }

    const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
    const apiDistributions = (series as any).service_auto_distributions;
    const formattedDistributions: any[] = [];

    SERVICE_OPTIONS.forEach(service => {
      const apiServiceName = getApiServiceName(service.name);
      const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

      if (apiDist) {
        formattedDistributions.push({
          id: apiDist.id,
          service_name: service.name,
          environment: currentEnv,
          enabled: true,
          auto_publish: apiDist.auto_publish,
          subtitle_langs:
            apiDist.subtitle_langs ||
            (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
        });
      } else {
        formattedDistributions.push({
          service_name: service.name,
          environment: currentEnv,
          enabled: false,
          auto_publish: false,
          subtitle_langs: [],
        });
      }
    });

    return formattedDistributions;
  }, [series]);

  // Season의 distribution을 포맷팅하는 함수
  const formatSeasonDistributions = useMemo(() => {
    if (!season || !(season as any).service_auto_distributions) {
      return createDefaultDistributions();
    }

    const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
    const apiDistributions = (season as any).service_auto_distributions;
    const formattedDistributions: any[] = [];

    SERVICE_OPTIONS.forEach(service => {
      const apiServiceName = getApiServiceName(service.name);
      const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

      if (apiDist) {
        formattedDistributions.push({
          id: apiDist.id,
          service_name: service.name,
          environment: currentEnv,
          enabled: true,
          auto_publish: apiDist.auto_publish,
          subtitle_langs:
            apiDist.subtitle_langs ||
            (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
        });
      } else {
        formattedDistributions.push({
          service_name: service.name,
          environment: currentEnv,
          enabled: false,
          auto_publish: false,
          subtitle_langs: [],
        });
      }
    });

    return formattedDistributions;
  }, [season]);

  // Episode inherit일 때 표시할 distribution 계산
  const displayDistributions = useMemo(() => {
    if (!inherit) {
      // Episode가 inherit하지 않으면 Episode 자체 설정
      return distributions;
    }

    // Episode가 inherit하면 Season 확인
    if (!season) {
      return createDefaultDistributions();
    }

    const seasonInherit = (season as any).service_auto_distribution_inherit !== false;

    if (seasonInherit) {
      // Season이 inherit하면 Series 설정 사용
      return formatSeriesDistributions;
    } else {
      // Season이 inherit하지 않으면 Season 설정 사용
      return formatSeasonDistributions;
    }
  }, [inherit, season, distributions, formatSeriesDistributions, formatSeasonDistributions]);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchEpisode();
    } else {
      initializeDefaultDistributions();
      // 새 Episode인 경우 기본값 설정
      form.setFieldValue('on_air_date', dayjs()); // 현재 날짜를 기본값으로 설정
      // 새 Episode인 경우 series_id와 season_id가 있으면 정보 가져오기
      if (isPendingSeason) {
        // 시즌이 아직 저장되지 않은 경우, sessionStorage에서 데이터 가져오기
        loadPendingSeasonData();
      } else if (seriesId && seasonId) {
        fetchSeriesAndSeasonInfo(seriesId, seasonId);
      } else if (seasonId) {
        fetchSeasonInfo(seasonId);
      }
    }
    fetchWriteLanguages();
    // 초기 Video Asset 로드
    fetchVideoAssets();
  }, [id, seasonId, seriesId, isPendingSeason]);

  const loadPendingSeasonData = async () => {
    try {
      const pendingSeasonDataStr = sessionStorage.getItem('pendingSeasonData');
      if (!pendingSeasonDataStr) {
        console.warn('No pending season data found in sessionStorage');
        return;
      }

      const pendingSeasonData = JSON.parse(pendingSeasonDataStr);

      // 시리즈 정보 가져오기 (series_id가 있는 경우)
      if (pendingSeasonData.series_id) {
        try {
          const seriesData = await seriesService.fetchById(pendingSeasonData.series_id);
          setSeries(seriesData);
          form.setFieldValue('series_slug', seriesData.slug || '');
        } catch (error) {
          console.error('Failed to fetch series info:', error);
        }
      }

      // Episode Number 자동 설정
      try {
        // 시즌이 아직 저장되지 않았으므로 episode_order는 1로 설정
        form.setFieldValue('episode_order', 1);
      } catch (error) {
        console.error('Failed to set episode order:', error);
        form.setFieldValue('episode_order', 1);
      }

      // Episode Title 자동 생성 (언어별 포맷 적용)
      const episodeNum = form.getFieldValue('episode_order') ?? 1;
      const episodeTitle: I18nFieldData = {};
      let episodeSlug = '';
      if (pendingSeasonData.i18n_title?.en && episodeNum !== null && episodeNum !== undefined) {
        episodeTitle.en = `${pendingSeasonData.i18n_title.en} : E${episodeNum}`;
      }
      if (pendingSeasonData.i18n_title?.ko && episodeNum !== null && episodeNum !== undefined) {
        episodeTitle.ko = `${pendingSeasonData.i18n_title.ko} : ${episodeNum}회`;
      }
      if (
        pendingSeasonData.i18n_title?.['zh-hans'] &&
        episodeNum !== null &&
        episodeNum !== undefined
      ) {
        episodeTitle['zh-hans'] = `${pendingSeasonData.i18n_title['zh-hans']} : 第${episodeNum}集`;
      }
      if (
        (pendingSeasonData.i18n_title as any)?.vi &&
        episodeNum !== null &&
        episodeNum !== undefined
      ) {
        (episodeTitle as any).vi = `${
          (pendingSeasonData.i18n_title as any).vi
        } : Tập ${episodeNum}`;
      }
      if ((pendingSeasonData as any).slug && episodeNum !== null && episodeNum !== undefined) {
        episodeSlug = `${(pendingSeasonData as any).slug}-e${episodeNum}`;
        form.setFieldValue('slug', episodeSlug);
      }
      setI18nTitle(episodeTitle);
      form.setFieldsValue({
        episode_title_en: episodeTitle.en || '',
        episode_title_ko: episodeTitle.ko || '',
        episode_title_zh_hans: episodeTitle['zh-hans'] || '',
        episode_title_vi: (episodeTitle as any).vi || '',
      });

      // 시즌의 SEO 정보 복사
      if (pendingSeasonData.i18n_seo_title) {
        setI18nSeoTitle(pendingSeasonData.i18n_seo_title);
      }
      if (pendingSeasonData.i18n_seo_synopsis) {
        setI18nSeoSynopsis(pendingSeasonData.i18n_seo_synopsis);
      }
      if (pendingSeasonData.i18n_seo_keyword) {
        setI18nSeoKeyword(pendingSeasonData.i18n_seo_keyword);
      }

      // 시즌의 Service Auto Distribution 설정 복사
      const inheritValue = pendingSeasonData.service_auto_distribution_inherit;
      setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

      if (
        pendingSeasonData.service_auto_distributions &&
        pendingSeasonData.service_auto_distributions.length > 0
      ) {
        const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
        const apiDistributions = pendingSeasonData.service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          const apiServiceName = getApiServiceName(service.name);
          const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

          if (apiDist) {
            formattedDistributions.push({
              id: apiDist.id,
              service_name: service.name,
              environment: currentEnv,
              enabled: true,
              auto_publish: apiDist.auto_publish,
              subtitle_langs:
                apiDist.subtitle_langs ||
                (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
            });
          } else {
            formattedDistributions.push({
              service_name: service.name,
              environment: currentEnv,
              enabled: false,
              auto_publish: false,
              subtitle_langs: [],
            });
          }
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }
    } catch (error) {
      console.error('Failed to load pending season data:', error);
    }
  };

  const fetchSeriesAndSeasonInfo = async (
    seriesId: number,
    seasonId: number,
    isNewEpisode: boolean = true
  ) => {
    try {
      // Series 정보 가져오기
      const seriesData = await seriesService.fetchById(seriesId);
      setSeries(seriesData);

      // Season 정보 가져오기
      const seasonData = await seasonService.fetchSeasonWithI18n(seasonId);
      setSeason(seasonData);

      // Form에 Series Title En, Season Title En, Season Number 설정
      form.setFieldValue('series_title_en', seriesData.i18n_title?.en || seriesData.title_en || '');
      form.setFieldValue('season_title_en', seasonData.i18n_title?.en || seasonData.title_en || '');
      form.setFieldValue('season_slug', seasonData.slug || '');
      form.setFieldValue('series_slug', seriesData.slug || '');
      form.setFieldValue('season_id', seasonData.id || '');
      form.setFieldValue(
        'season_number',
        seasonData.season_number || seasonData.season_order || ''
      );

      // Episode Number 자동 설정 (신규 생성 시에만: 가장 큰 episode number + 1)
      if (isNewEpisode) {
        try {
          // episode_order 내림차순으로 1개만 가져와서 max 값 확인 (전체 데이터 로드 방지)
          const maxOrderParams = new URLSearchParams();
          maxOrderParams.set('page', '1');
          maxOrderParams.set('page_size', '1');
          maxOrderParams.set('order_by', 'episode_order');
          maxOrderParams.set('order_direction', 'desc');
          const seasonContents = await seasonContentService.getSeasonContents(
            seasonId,
            maxOrderParams
          );
          const maxEpisodeOrder =
            seasonContents.contents.length > 0 && seasonContents.contents[0].episode_order
              ? seasonContents.contents[0].episode_order
              : 0;
          const nextEpisodeOrder = maxEpisodeOrder + 1;
          form.setFieldValue('episode_order', nextEpisodeOrder);
        } catch (error) {
          console.error('Failed to fetch season contents:', error);
          // 에러 발생 시 기본값 1로 설정
          form.setFieldValue('episode_order', 1);
        }

        // Episode Title 자동 생성 (언어별 포맷 적용)
        const episodeNum = form.getFieldValue('episode_order');
        const episodeTitle: I18nFieldData = {};
        if (seasonData.i18n_title?.en && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle.en = `${seasonData.i18n_title.en} : E${episodeNum}`;
        }
        if (seasonData.i18n_title?.ko && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle.ko = `${seasonData.i18n_title.ko} : ${episodeNum}회`;
        }
        if (seasonData.i18n_title?.['zh-hans'] && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle['zh-hans'] = `${seasonData.i18n_title['zh-hans']} : 第${episodeNum}集`;
        }
        if ((seasonData.i18n_title as any)?.vi && episodeNum !== null && episodeNum !== undefined) {
          (episodeTitle as any).vi = `${(seasonData.i18n_title as any).vi} : Tập ${episodeNum}`;
        }
        setI18nTitle(episodeTitle);
        form.setFieldsValue({
          episode_title_en: episodeTitle.en || '',
          episode_title_ko: episodeTitle.ko || '',
          episode_title_zh_hans: episodeTitle['zh-hans'] || '',
          episode_title_vi: (episodeTitle as any).vi || '',
        });

        if (seasonData.slug && episodeNum !== null && episodeNum !== undefined) {
          form.setFieldValue('slug', `${seasonData.slug}-e${episodeNum}`);
        }

        // 시즌의 SEO 정보 복사
        if (seasonData.i18n_seo_title) {
          setI18nSeoTitle(seasonData.i18n_seo_title);
        }
        if (seasonData.i18n_seo_synopsis) {
          setI18nSeoSynopsis(seasonData.i18n_seo_synopsis);
        }
        if (seasonData.i18n_seo_keyword) {
          setI18nSeoKeyword(seasonData.i18n_seo_keyword);
        }

        // 시즌의 Service Auto Distribution 설정 복사
        const inheritValue = (seasonData as any).service_auto_distribution_inherit;
        setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

        if (
          (seasonData as any).service_auto_distributions &&
          (seasonData as any).service_auto_distributions.length > 0
        ) {
          const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
          const apiDistributions = (seasonData as any).service_auto_distributions;
          const formattedDistributions: any[] = [];

          SERVICE_OPTIONS.forEach(service => {
            const apiServiceName = getApiServiceName(service.name);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: currentEnv,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: currentEnv,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });

          setDistributions(formattedDistributions);
        } else {
          initializeDefaultDistributions();
        }
      }
    } catch (error) {
      console.error('Failed to fetch series and season info:', error);
    }
  };

  const fetchSeasonInfo = async (seasonId: number, isNewEpisode: boolean = true) => {
    try {
      const seasonData = await seasonService.fetchSeasonWithI18n(seasonId);
      setSeason(seasonData);
      if (seasonData.series) {
        const seriesData = await seriesService.fetchById(Number(seasonData.series));
        form.setFieldValue('series_slug', seriesData.slug || '');
        setSeries(seriesData);
      }

      // Form에 Season Title En, Season Number 설정
      form.setFieldValue('season_title_en', seasonData.i18n_title?.en || seasonData.title_en || '');
      form.setFieldValue('season_slug', seasonData.slug || '');
      form.setFieldValue(
        'season_number',
        seasonData.season_number || seasonData.season_order || ''
      );
      form.setFieldValue('season_id', seasonData.id || '');

      // Episode Number 자동 설정 (신규 생성 시에만: 가장 큰 episode number + 1)
      if (isNewEpisode) {
        try {
          // episode_order 내림차순으로 1개만 가져와서 max 값 확인 (전체 데이터 로드 방지)
          const maxOrderParams = new URLSearchParams();
          maxOrderParams.set('page', '1');
          maxOrderParams.set('page_size', '1');
          maxOrderParams.set('order_by', 'episode_order');
          maxOrderParams.set('order_direction', 'desc');
          const seasonContents = await seasonContentService.getSeasonContents(
            seasonId,
            maxOrderParams
          );
          const maxEpisodeOrder =
            seasonContents.contents.length > 0 && seasonContents.contents[0].episode_order
              ? seasonContents.contents[0].episode_order
              : 0;
          const nextEpisodeOrder = maxEpisodeOrder + 1;
          form.setFieldValue('episode_order', nextEpisodeOrder);
        } catch (error) {
          console.error('Failed to fetch season contents:', error);
          // 에러 발생 시 기본값 1로 설정
          form.setFieldValue('episode_order', 1);
        }

        // Episode Title 자동 생성 (언어별 포맷 적용)
        const episodeNum = form.getFieldValue('episode_order');
        const episodeTitle: I18nFieldData = {};
        if (seasonData.i18n_title?.en && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle.en = `${seasonData.i18n_title.en} : E${episodeNum}`;
        }
        if (seasonData.i18n_title?.ko && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle.ko = `${seasonData.i18n_title.ko} : ${episodeNum}회`;
        }
        if (seasonData.i18n_title?.['zh-hans'] && episodeNum !== null && episodeNum !== undefined) {
          episodeTitle['zh-hans'] = `${seasonData.i18n_title['zh-hans']} : 第${episodeNum}集`;
        }
        if ((seasonData.i18n_title as any)?.vi && episodeNum !== null && episodeNum !== undefined) {
          (episodeTitle as any).vi = `${(seasonData.i18n_title as any).vi} : Tập ${episodeNum}`;
        }
        setI18nTitle(episodeTitle);
        form.setFieldsValue({
          episode_title_en: episodeTitle.en || '',
          episode_title_ko: episodeTitle.ko || '',
          episode_title_zh_hans: episodeTitle['zh-hans'] || '',
          episode_title_vi: (episodeTitle as any).vi || '',
        });

        if (seasonData.slug && episodeNum !== null && episodeNum !== undefined) {
          form.setFieldValue('slug', `${seasonData.slug}-e${episodeNum}`);
        }

        // 시즌의 SEO 정보 복사
        if (seasonData.i18n_seo_title) {
          setI18nSeoTitle(seasonData.i18n_seo_title);
        }
        if (seasonData.i18n_seo_synopsis) {
          setI18nSeoSynopsis(seasonData.i18n_seo_synopsis);
        }
        if (seasonData.i18n_seo_keyword) {
          setI18nSeoKeyword(seasonData.i18n_seo_keyword);
        }

        // 시즌의 Service Auto Distribution 설정 복사
        const inheritValue = (seasonData as any).service_auto_distribution_inherit;
        setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

        if (
          (seasonData as any).service_auto_distributions &&
          (seasonData as any).service_auto_distributions.length > 0
        ) {
          const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
          const apiDistributions = (seasonData as any).service_auto_distributions;
          const formattedDistributions: any[] = [];

          SERVICE_OPTIONS.forEach(service => {
            const apiServiceName = getApiServiceName(service.name);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: currentEnv,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: currentEnv,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });

          setDistributions(formattedDistributions);
        } else {
          initializeDefaultDistributions();
        }
      }
    } catch (error) {
      console.error('Failed to fetch season info:', error);
    }
  };

  const fetchEpisode = async () => {
    try {
      const data = await episodeService.fetchById(Number(id));
      setEpisode(data);

      // Form 값 설정 (응답 데이터를 form 필드명에 맞게 매핑)
      // 응답 데이터는 Content 모델에서 오므로 episode_order, episode_part, title_en 등이 포함됨
      const responseData = data as any;
      const formattedData = {
        slug: data.slug,
        episode_order: responseData.episode_order, // episode_order -> episode_number로 매핑
        episode_part_number: responseData.episode_part, // episode_part -> episode_part_number로 매핑
        on_air_date: data.on_air_date ? dayjs(data.on_air_date) : null,
        status: data.status === 'published' ? 'published' : 'unpublished',
        // content_type을 episode_type 필드에 설정
        episode_type: (data as any).content_type || 'tv-series',
        // i18n title 필드들을 form 필드명에 맞게 설정
        episode_title_en: data.i18n_title?.en || responseData.title_en || '',
        episode_title_ko: data.i18n_title?.ko || '',
        episode_title_zh_hans: data.i18n_title?.['zh-hans'] || '',
        episode_title_vi: (data.i18n_title as any)?.vi || '',
        // secondary title 필드들
        secondary_episode_title_en: data.i18n_secondary_title?.en || '',
        secondary_episode_title_ko: data.i18n_secondary_title?.ko || '',
        secondary_episode_title_zh_hans: data.i18n_secondary_title?.['zh-hans'] || '',
        secondary_episode_title_vi: (data.i18n_secondary_title as any)?.vi || '',
        // CP from ODX
        cp_from_odx: (data as any).cp_from_odx || '',
      };
      form.setFieldsValue(formattedData);

      // i18n 필드 초기화
      if (data.i18n_title) setI18nTitle(data.i18n_title);
      if (data.i18n_secondary_title) setI18nSecondaryTitle(data.i18n_secondary_title);
      if (data.i18n_seo_title) setI18nSeoTitle(data.i18n_seo_title);
      if (data.i18n_seo_synopsis) setI18nSeoSynopsis(data.i18n_seo_synopsis);
      if (data.i18n_seo_keyword) setI18nSeoKeyword(data.i18n_seo_keyword);

      // Season 정보 가져오기 (season 필드 또는 season_id 필드 확인)
      // 기존 에피소드 편집 시에는 episode_order를 덮어쓰지 않도록 isNewEpisode=false 전달
      const seasonIdToFetch = (data as any).season || data.season_id;

      if (seasonIdToFetch) {
        await fetchSeasonInfo(seasonIdToFetch, false);
      }

      // Service Auto Distribution 설정
      const inheritValue = data.service_auto_distribution_inherit;
      setInherit(inheritValue !== false); // undefined나 true면 상속, false면 자체 설정

      if (data.service_auto_distributions && data.service_auto_distributions.length > 0) {
        const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
        const apiDistributions = data.service_auto_distributions;
        const formattedDistributions: any[] = [];

        SERVICE_OPTIONS.forEach(service => {
          const apiServiceName = getApiServiceName(service.name);
          const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

          if (apiDist) {
            formattedDistributions.push({
              id: apiDist.id,
              service_name: service.name,
              environment: currentEnv,
              enabled: true,
              auto_publish: apiDist.auto_publish,
              subtitle_langs:
                apiDist.subtitle_langs ||
                (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
            });
          } else {
            formattedDistributions.push({
              service_name: service.name,
              environment: currentEnv,
              enabled: false,
              auto_publish: false,
              subtitle_langs: [],
            });
          }
        });

        setDistributions(formattedDistributions);
      } else {
        initializeDefaultDistributions();
      }

      // Linked Video Asset 가져오기
      if (data.video_asset_id) {
        try {
          // videoService는 ApiService를 상속받으므로 fetchById 사용 가능
          const videoAsset = await (videoService as any).fetchById(data.video_asset_id);
          setLinkedVideoAsset(videoAsset);
        } catch (error) {
          console.error('Failed to fetch linked video asset:', error);
        }
      }

      // Content Video Assets 가져오기
      try {
        const contentAssets = await contentAssetService.fetchContentVideoAssets(Number(id));
        setExistingContentVideoAssets(contentAssets);

        // content video assets의 subtitle 정보를 가져오기 위해 video별로 subtitles fetch
        const contentAssetsWithSubtitles = await Promise.all(
          contentAssets.map(async asset => {
            if (asset.video) {
              try {
                const subtitles = await videoSubtitleService.fetchByVideoId(asset.video);
                return { ...asset, subtitles };
              } catch (error) {
                console.error(`Failed to fetch subtitles for video ${asset.video}:`, error);
                return asset;
              }
            }
            return asset;
          })
        );

        // 각 content video asset의 video 정보를 가져와서 linkedVideoAssetsData 설정
        const linkedAssetsWithVideoData = await Promise.all(
          contentAssetsWithSubtitles.map(async asset => {
            if (asset.video) {
              try {
                const videoAsset = await (videoService as any).fetchById(asset.video);
                return {
                  ...asset,
                  videoData: videoAsset,
                };
              } catch (error) {
                console.error(`Failed to fetch video asset ${asset.video}:`, error);
                return asset;
              }
            }
            return asset;
          })
        );
        setLinkedVideoAssetsData(linkedAssetsWithVideoData);

        // video_id 기준으로 그룹화하여 primary인 비디오 찾기
        const videoGroups = new Map<number, typeof linkedAssetsWithVideoData>();
        linkedAssetsWithVideoData.forEach(asset => {
          if (asset.video) {
            if (!videoGroups.has(asset.video)) {
              videoGroups.set(asset.video, []);
            }
            videoGroups.get(asset.video)!.push(asset);
          }
        });

        // primary인 비디오 찾기
        let primaryVideoId: number | null = null;
        for (const [videoId, assets] of videoGroups.entries()) {
          if (assets.some(a => a.primary)) {
            primaryVideoId = videoId;
            break;
          }
        }

        // primary 비디오 또는 첫 번째 비디오를 linkedVideoAsset으로 설정
        if (primaryVideoId || linkedAssetsWithVideoData.length > 0) {
          const targetVideoId = primaryVideoId || linkedAssetsWithVideoData[0].video;
          if (targetVideoId) {
            const targetAsset = linkedAssetsWithVideoData.find(a => a.video === targetVideoId);
            if (targetAsset && (targetAsset as any).videoData) {
              setLinkedVideoAsset((targetAsset as any).videoData);
              form.setFieldValue('video_asset_id', targetVideoId);
              // subtitle 정보 설정
              try {
                const subtitles = await videoSubtitleService.fetchByVideoId(targetVideoId);
                setLinkedVideoAssetSubtitles(subtitles);
              } catch (error) {
                console.error(`Failed to fetch subtitles for video ${targetVideoId}:`, error);
                setLinkedVideoAssetSubtitles([]);
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch content video assets:', error);
        // 에러가 발생해도 계속 진행
      }

      // Thumbnail Override 로드
      try {
        const overrides = await thumbnailOverrideService.fetchList(Number(id));
        const overrideMap = new Map<string, ContentThumbnailOverride>();
        overrides.forEach(override => {
          // API에서 language가 대문자로 올 수 있으므로 소문자로 통일
          const languageKey = override.language.toLowerCase();
          overrideMap.set(languageKey, override);
        });
        setThumbnailOverrides(overrideMap);
      } catch (error) {
        console.error('Failed to fetch thumbnail overrides:', error);
        // 에러가 발생해도 계속 진행
      }
    } catch (error) {
      console.error('Failed to fetch episode:', error);
      message.error('Failed to load episode data');
    }
  };

  const fetchWriteLanguages = async () => {
    try {
      const response = await globalSettingService.fetchLanguages(
        new URLSearchParams({
          type: 'write',
          page_size: '100',
          order_by: 'order',
          order_direction: 'asc',
        })
      );
      setWriteLanguages(response.results);
    } catch (error) {
      console.error('Failed to fetch write languages:', error);
    }
  };

  const fetchVideoAssets = async (searchKeyword?: string, page: number = 1) => {
    try {
      setIsLoadingVideoAssets(true);
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '10',
      });
      if (searchKeyword && searchKeyword.trim()) {
        params.set('search', searchKeyword.trim());
      }
      const response = await videoService.fetchVideos(params);
      setVideoAssets(response.data || []);
      setVideoAssetTotal(response.count || 0);
      setVideoAssetCurrentPage(page);
    } catch (error) {
      console.error('Failed to search video assets:', error);
      message.error('Failed to search video assets');
    } finally {
      setIsLoadingVideoAssets(false);
    }
  };

  const handleVideoAssetSearch = async () => {
    setVideoAssetCurrentPage(1);
    await fetchVideoAssets(videoAssetKeyword, 1);
  };

  const handleVideoAssetPageChange = (page: number) => {
    fetchVideoAssets(videoAssetKeyword, page);
  };

  // 기본 시즌 목록 로드
  useEffect(() => {
    const loadDefaultSeasons = async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('page_size', '100');

        const result = await seasonService.fetchSeasonsWithI18n(queryParams);
        setDefaultSeasons(result.results || []);
        setSearchSeasons(result.results || []);
      } catch (error) {
        console.error('Failed to load default seasons:', error);
      }
    };

    if (seasonSearchModalVisible) {
      loadDefaultSeasons();
    }
  }, [seasonSearchModalVisible]);

  // 시즌 검색 함수 (debounce 적용)
  const debouncedSearchSeasons = useCallback(
    debounce(async (searchTerm: string) => {
      if (!searchTerm.trim()) {
        setSearchSeasons(defaultSeasons);
        return;
      }

      setSeasonSearchLoading(true);
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('search', searchTerm);
        queryParams.append('page_size', '50');

        const result = await seasonService.fetchSeasonsWithI18n(queryParams);
        setSearchSeasons(result.results || []);
      } catch (error) {
        console.error('Season search failed:', error);
        setSearchSeasons([]);
      } finally {
        setSeasonSearchLoading(false);
      }
    }, 300),
    [defaultSeasons]
  );

  // 시즌 검색어 변경 처리
  const handleSeasonSearch = (value: string) => {
    setSeasonSearchKeyword(value);
    if (!value.trim()) {
      setSearchSeasons(defaultSeasons);
    } else {
      debouncedSearchSeasons(value);
    }
  };

  // 시즌 선택 처리
  const handleSelectSeason = async (selectedSeason: SeasonData) => {
    try {
      setSeason(selectedSeason);

      // Form에 Season 정보 설정
      form.setFieldValue(
        'season_title_en',
        selectedSeason.i18n_title?.en || selectedSeason.title_en || ''
      );
      form.setFieldValue('season_slug', selectedSeason.slug || '');
      form.setFieldValue(
        'season_number',
        selectedSeason.season_number || selectedSeason.season_order || ''
      );
      form.setFieldValue('season_id', selectedSeason.id || '');

      // Series 정보도 가져오기
      if (selectedSeason.series) {
        const seriesData = await seriesService.fetchById(Number(selectedSeason.series));
        setSeries(seriesData);
        form.setFieldValue('series_slug', seriesData.slug || '');
      }

      // Episode Number 자동 설정 (신규 생성 시에만)
      if (id === 'new' || !id) {
        let nextEpisodeOrder = 1;
        try {
          // episode_order 내림차순으로 1개만 가져와서 max 값 확인 (전체 데이터 로드 방지)
          const maxOrderParams = new URLSearchParams();
          maxOrderParams.set('page', '1');
          maxOrderParams.set('page_size', '1');
          maxOrderParams.set('order_by', 'episode_order');
          maxOrderParams.set('order_direction', 'desc');
          const seasonContents = await seasonContentService.getSeasonContents(
            selectedSeason.id,
            maxOrderParams
          );
          const maxEpisodeOrder =
            seasonContents.contents.length > 0 && seasonContents.contents[0].episode_order
              ? seasonContents.contents[0].episode_order
              : 0;
          nextEpisodeOrder = maxEpisodeOrder + 1;
          form.setFieldValue('episode_order', nextEpisodeOrder);
        } catch (error) {
          console.error('Failed to fetch season contents:', error);
          form.setFieldValue('episode_order', nextEpisodeOrder);
        }

        // Episode Title 자동 생성 (언어별 포맷 적용)
        const episodeTitle: I18nFieldData = {};
        if (
          selectedSeason.i18n_title?.en &&
          nextEpisodeOrder !== null &&
          nextEpisodeOrder !== undefined
        ) {
          episodeTitle.en = `${selectedSeason.i18n_title.en} : E${nextEpisodeOrder}`;
        }
        if (
          selectedSeason.i18n_title?.ko &&
          nextEpisodeOrder !== null &&
          nextEpisodeOrder !== undefined
        ) {
          episodeTitle.ko = `${selectedSeason.i18n_title.ko} : ${nextEpisodeOrder}회`;
        }
        if (
          selectedSeason.i18n_title?.['zh-hans'] &&
          nextEpisodeOrder !== null &&
          nextEpisodeOrder !== undefined
        ) {
          episodeTitle['zh-hans'] = `${
            selectedSeason.i18n_title['zh-hans']
          } : 第${nextEpisodeOrder}集`;
        }
        if (
          (selectedSeason.i18n_title as any)?.vi &&
          nextEpisodeOrder !== null &&
          nextEpisodeOrder !== undefined
        ) {
          (episodeTitle as any).vi = `${
            (selectedSeason.i18n_title as any).vi
          } : Tập ${nextEpisodeOrder}`;
        }

        // handleTitleChange를 호출하여 slug도 자동 생성되도록 함
        handleTitleChange(episodeTitle);
        const newSlug = slugify(`${selectedSeason.slug}: E${nextEpisodeOrder}`);
        form.setFieldValue('slug', newSlug);

        // 시즌의 SEO 정보 복사
        if (selectedSeason.i18n_seo_title) {
          setI18nSeoTitle(selectedSeason.i18n_seo_title);
        }
        if (selectedSeason.i18n_seo_synopsis) {
          setI18nSeoSynopsis(selectedSeason.i18n_seo_synopsis);
        }
        if (selectedSeason.i18n_seo_keyword) {
          setI18nSeoKeyword(selectedSeason.i18n_seo_keyword);
        }

        // 시즌의 Service Auto Distribution 설정 복사
        const inheritValue = (selectedSeason as any).service_auto_distribution_inherit;
        setInherit(inheritValue !== false);

        if (
          (selectedSeason as any).service_auto_distributions &&
          (selectedSeason as any).service_auto_distributions.length > 0
        ) {
          const currentEnv = process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';
          const apiDistributions = (selectedSeason as any).service_auto_distributions;
          const formattedDistributions: any[] = [];

          SERVICE_OPTIONS.forEach(service => {
            const apiServiceName = getApiServiceName(service.name);
            const apiDist = apiDistributions.find((d: any) => d.service_name === apiServiceName);

            if (apiDist) {
              formattedDistributions.push({
                id: apiDist.id,
                service_name: service.name,
                environment: currentEnv,
                enabled: true,
                auto_publish: apiDist.auto_publish,
                subtitle_langs:
                  apiDist.subtitle_langs ||
                  (apiDist.subtitles ? apiDist.subtitles.map((s: any) => s.subtitle_lang) : []),
              });
            } else {
              formattedDistributions.push({
                service_name: service.name,
                environment: currentEnv,
                enabled: false,
                auto_publish: false,
                subtitle_langs: [],
              });
            }
          });

          setDistributions(formattedDistributions);
        } else {
          initializeDefaultDistributions();
        }
      }

      setSeasonSearchModalVisible(false);
      setSeasonSearchKeyword('');
      message.success('Season selected successfully');
    } catch (error) {
      console.error('Failed to select season:', error);
      message.error('Failed to select season');
    }
  };

  const handleLinkVideoAssets = async () => {
    if (selectedVideoAssets.length === 0) {
      message.warning('Please select a video asset to link');
      return;
    }

    if (selectedVideoAssets.length > 1) {
      message.warning('Please select only one video asset');
      return;
    }

    // 선택된 비디오 에셋 1개만 가져오기
    const selectedVideoId = selectedVideoAssets[0];
    const newVideoAsset = videoAssets.find(v => v.id === selectedVideoId);

    if (!newVideoAsset) {
      message.error('Selected video asset not found');
      return;
    }

    // 이미 링크된 비디오인지 확인 (video_id 기준)
    const isAlreadyLinked = linkedVideoAssetsData.some(asset => asset.video === selectedVideoId);
    if (isAlreadyLinked) {
      message.warning('This video asset is already linked');
      setSelectedVideoAssets([]);
      return;
    }

    // 새 content video asset 생성 (is_default: true, primary: false로 시작)
    const newLinkedAsset: VideoAssetSet & { videoData?: VideoType } = {
      content: id && id !== 'new' ? Number(id) : 0,
      video: newVideoAsset.id,
      subtitle_ids: null,
      is_subtitles_embedded: false,
      is_default: true, // link 버튼 클릭시 is_default는 true로 설정
      primary: false, // 초기에는 false, 나중에 primary 선택 가능
      service_name: '',
      stream_manifests: null,
      after_add_subtitle: false,
      videoData: newVideoAsset,
    };

    // 기존 에셋 목록에 추가 (로컬 상태만 업데이트)
    // Save 버튼 클릭 시 실제 저장이 수행됨
    setLinkedVideoAssetsData([...linkedVideoAssetsData, newLinkedAsset]);
    // 새로 링크된 에셋을 자동으로 선택
    setSelectedVideoAssets([newVideoAsset.id]);

    // linkedVideoAsset과 subtitle 정보 업데이트
    setLinkedVideoAsset(newVideoAsset);
    try {
      const subtitles = await videoSubtitleService.fetchByVideoId(newVideoAsset.id);
      setLinkedVideoAssetSubtitles(subtitles);
    } catch (error) {
      console.error(`Failed to fetch subtitles for video ${newVideoAsset.id}:`, error);
      setLinkedVideoAssetSubtitles([]);
    }

    message.success('Video asset will be linked when you save');
  };

  const handleUnlinkVideoAssets = () => {
    if (selectedVideoAssets.length === 0) {
      message.warning('Please select a video asset to unlink');
      return;
    }

    if (selectedVideoAssets.length > 1) {
      message.warning('Please select only one video asset');
      return;
    }

    // 선택된 비디오 ID
    const selectedVideoId = selectedVideoAssets[0];

    // 해당 video_id를 가진 모든 content_video_asset 찾기
    const assetsToRemove = linkedVideoAssetsData.filter(asset => asset.video === selectedVideoId);

    if (assetsToRemove.length === 0) {
      message.warning('Selected video asset not found in linked assets');
      setSelectedVideoAssets([]);
      return;
    }

    // 로컬 상태에서 제거 (video_id 기준으로 모든 에셋 제거)
    // Save 버튼 클릭 시 실제 삭제가 수행됨
    const updatedLinkedAssets = linkedVideoAssetsData.filter(
      asset => asset.video !== selectedVideoId
    );

    // 제거 후 남은 에셋이 있으면 primary인 비디오 또는 첫 번째 비디오 선택
    if (updatedLinkedAssets.length > 0) {
      // primary인 비디오 찾기
      const primaryAsset = updatedLinkedAssets.find(a => a.primary);
      const targetAsset = primaryAsset || updatedLinkedAssets[0];

      if (targetAsset.video) {
        setSelectedVideoAssets([targetAsset.video]);
        // subtitle 정보 업데이트
        if (targetAsset.videoData) {
          setLinkedVideoAsset(targetAsset.videoData);
          // subtitle 정보는 videoData에서 가져오거나 비동기로 가져올 수 있음
          videoSubtitleService
            .fetchByVideoId(targetAsset.video)
            .then(subtitles => {
              setLinkedVideoAssetSubtitles(subtitles);
            })
            .catch(error => {
              console.error(`Failed to fetch subtitles for video ${targetAsset.video}:`, error);
              setLinkedVideoAssetSubtitles([]);
            });
        }
      }
    } else {
      // 남은 에셋이 없으면 선택 초기화
      setSelectedVideoAssets([]);
      setLinkedVideoAsset(null);
      setLinkedVideoAssetSubtitles([]);
    }

    setLinkedVideoAssetsData(updatedLinkedAssets);
    message.success('Video asset will be unlinked when you save');
  };

  const handlePrimaryChange = (videoId: number, checked: boolean) => {
    if (!checked) {
      // 다른 비디오가 primary로 선택되어 있는지 확인
      const hasOtherPrimary = linkedVideoAssetsData.some(
        asset => asset.video !== videoId && asset.primary
      );
      if (!hasOtherPrimary) {
        message.warning('Primary must be selected for at least one video');
        return;
      }
      // 다른 비디오가 primary로 선택되어 있으면 해제 허용 (로컬 상태만 업데이트)
      const updatedLinkedAssets = linkedVideoAssetsData.map(asset => ({
        ...asset,
        primary: asset.video === videoId ? false : asset.primary,
      }));
      setLinkedVideoAssetsData(updatedLinkedAssets);
    } else {
      // 해당 video_id를 가진 모든 content_video_asset의 primary를 true로 설정
      // 다른 video_id를 가진 모든 content_video_asset의 primary를 false로 설정
      // (로컬 상태만 업데이트, Save 버튼 클릭 시 저장됨)
      const updatedLinkedAssets = linkedVideoAssetsData.map(asset => ({
        ...asset,
        primary: asset.video === videoId ? true : false,
      }));

      setLinkedVideoAssetsData(updatedLinkedAssets);
    }
  };

  // Title 변경 핸들러 (slug에는 영향 없음)
  const handleTitleChange = (value: I18nFieldData) => {
    setI18nTitle(value);
    // slug는 title 변경에 영향을 받지 않음
  };

  // Slug 업데이트 함수 (episode_order와 episode_part 모두 고려)
  const updateSlug = () => {
    if (!season?.slug) return;

    const episodeOrder = form.getFieldValue('episode_order');
    const episodePart = form.getFieldValue('episode_part_number');

    if (episodeOrder !== null && episodeOrder !== undefined) {
      let slugBase = `${season.slug}: E${episodeOrder}`;

      // episode_part가 있으면 -{episode_part} 추가
      if (episodePart !== null && episodePart !== undefined && episodePart !== '') {
        slugBase += `-${episodePart}`;
      }

      const newSlug = slugify(slugBase);
      form.setFieldValue('slug', newSlug);
    }
  };

  // Episode Number 변경 핸들러 (slug만 업데이트, title에는 영향 없음)
  const handleEpisodeNumberChange = (value: number | null) => {
    form.setFieldValue('episode_order', value);
    updateSlug();
  };

  // Episode Part Number 변경 핸들러 (slug만 업데이트)
  const handleEpisodePartNumberChange = (value: number | null) => {
    form.setFieldValue('episode_part_number', value);
    updateSlug();
  };

  // Thumbnail Override 핸들러들
  const handleThumbnailUpload = (language: string, file: File) => {
    // 파일 미리보기 생성
    const reader = new FileReader();
    reader.onload = e => {
      setUploadedThumbnails(prev => new Map(prev).set(language, e.target?.result as string));
    };
    reader.readAsDataURL(file);

    // 파일 정보를 저장해두기 위해 uploadedThumbnails의 값으로 파일 객체를 저장
    // 실제로는 별도 Map을 사용해야 하지만 간단하게 처리
    (window as any).__thumbnailFiles = (window as any).__thumbnailFiles || {};
    (window as any).__thumbnailFiles[language] = file;
  };

  const handleDeleteThumbnailOverride = async (language: string) => {
    if (!id) return;

    const existingOverride = thumbnailOverrides.get(language);
    if (!existingOverride) {
      // 업로드만 한 상태에서 삭제
      setUploadedThumbnails(prev => {
        const newMap = new Map(prev);
        newMap.delete(language);
        return newMap;
      });
      if ((window as any).__thumbnailFiles) {
        delete (window as any).__thumbnailFiles[language];
      }
      return;
    }

    try {
      await thumbnailOverrideService.delete(Number(id), existingOverride.id);
      message.success(`${language.toUpperCase()} thumbnail override deleted`);

      // 상태 업데이트
      setThumbnailOverrides(prev => {
        const newMap = new Map(prev);
        newMap.delete(language);
        return newMap;
      });
      setUploadedThumbnails(prev => {
        const newMap = new Map(prev);
        newMap.delete(language);
        return newMap;
      });
    } catch (error) {
      console.error('Failed to delete thumbnail override:', error);
      message.error('Failed to delete thumbnail override');
    }
  };

  const handleSaveAllThumbnailOverrides = async () => {
    if (!id) return;

    const files = (window as any).__thumbnailFiles || {};
    const languages = Object.keys(files);

    if (languages.length === 0) {
      message.warning('No new thumbnails to save');
      return;
    }

    setSavingThumbnails(true);
    try {
      for (const language of languages) {
        const file = files[language];
        await thumbnailOverrideService.createWithFile(Number(id), file, language, 'landscape');
      }

      message.success('All thumbnail overrides saved successfully');

      // 저장 후 다시 불러오기
      const overrides = await thumbnailOverrideService.fetchList(Number(id));
      const overrideMap = new Map<string, ContentThumbnailOverride>();
      overrides.forEach(override => {
        // API에서 language가 대문자로 올 수 있으므로 소문자로 통일
        const languageKey = override.language.toLowerCase();
        overrideMap.set(languageKey, override);
      });
      setThumbnailOverrides(overrideMap);

      // 임시 데이터 초기화
      setUploadedThumbnails(new Map());
      (window as any).__thumbnailFiles = {};
    } catch (error) {
      console.error('Failed to save thumbnail overrides:', error);
      message.error('Failed to save thumbnail overrides');
    } finally {
      setSavingThumbnails(false);
    }
  };

  const handleViewActionHistory = async () => {
    if (!id || id === 'new') {
      message.warning('Action history is available only for saved episodes');
      return;
    }
    setActionHistoryModalVisible(true);
    setActionHistoryLoading(true);
    try {
      const result = await actionHistoryService.fetchByParent('content', Number(id));
      setActionHistories(result.data);
    } catch (error) {
      console.error('Failed to fetch action history:', error);
      message.error('Failed to load action history');
    } finally {
      setActionHistoryLoading(false);
    }
  };

  const handleSave = async () => {
    // 하나의 Save 작업에서 발생하는 모든 API 호출을 동일한 action_session_id로 묶기
    const actionSessionId = crypto.randomUUID();
    setActionSessionId(actionSessionId);
    try {
      await form.validateFields();
      const formData = form.getFieldsValue();

      // Prepare i18n data in correct structure (Movie와 동일한 방식)
      const i18nStructuredData = {
        ...formData,
        i18n: {
          title: i18nTitle,
          secondary_title: i18nSecondaryTitle,
          seo_title: i18nSeoTitle,
          seo_synopsis: i18nSeoSynopsis,
          seo_keyword: i18nSeoKeyword,
        },
      };

      // Sync i18n data with legacy data (I18nService 사용)
      const finalFormData = I18nService.processMixedFormatData(i18nStructuredData);

      // Create API request data (including i18n fields)
      const apiRequest = I18nService.createApiRequest(finalFormData);

      // Final episode data (시리얼라이저에 맞게 필드 정리)
      // 불필요한 레거시 필드 제거하고 필요한 필드만 명시적으로 포함
      const episodeData: Partial<EpisodeInterface> & {
        content_type?: string;
        season?: number | null;
        episode_order?: number;
        episode_part?: number | null;
        title?: string;
        title_en?: string;
        i18n_secondary_title?: I18nFieldData;
        on_air_date?: string | null;
        cp_from_odx?: string;
      } = {
        // 기본 필드들
        slug: formData.slug,
        content_type: formData.episode_type || 'tv-series',
        status:
          formData.status === true || formData.status === 'published' ? 'published' : 'unpublished',

        // i18n 필드들 (apiRequest에서 가져옴)
        ...apiRequest,
        i18n_secondary_title: i18nSecondaryTitle || {}, // 시리얼라이저에서 지원하는 필드

        // Episode 관련 필드들 (시리얼라이저 필드명 사용)
        // season 필드 설정: 우선순위 1) URL 파라미터 2) episode의 season 필드 3) episode의 season_id 필드 4) season 상태의 id 5) null
        season: seasonId || (episode as any)?.season || episode?.season_id || season?.id || null,
        episode_order:
          formData.episode_order !== null &&
          formData.episode_order !== undefined &&
          formData.episode_order !== ''
            ? Number(formData.episode_order)
            : undefined, // episode_order 필드 사용
        episode_part: formData.episode_part_number ? Number(formData.episode_part_number) : null, // episode_part 필드 사용

        // 날짜 필드 (dayjs 객체를 문자열로 변환)
        on_air_date: formData.on_air_date
          ? dayjs.isDayjs(formData.on_air_date)
            ? formData.on_air_date.format('YYYY-MM-DD')
            : typeof formData.on_air_date === 'string'
              ? formData.on_air_date
              : dayjs(formData.on_air_date).format('YYYY-MM-DD')
          : null,

        // CP from ODX
        cp_from_odx: formData.cp_from_odx || '',

        service_auto_distribution_inherit: inherit,
        service_auto_distributions: !inherit
          ? (() => {
              // 현재 환경에 맞는 distribution만 필터링
              const currentEnvironment =
                process.env.REACT_APP_ENV === 'production' ? 'production' : 'staging';

              // 같은 service_name을 가진 항목들을 하나로 합치기 (중복 방지)
              const distributionMap = new Map<
                string,
                {
                  service_name: string;
                  auto_publish: boolean;
                  subtitle_langs: string[];
                  id?: number;
                }
              >();

              distributions
                .filter(dist => dist.enabled && dist.environment === currentEnvironment)
                .forEach(dist => {
                  const apiServiceName = getApiServiceName(dist.service_name);
                  const existing = distributionMap.get(apiServiceName);

                  if (!existing || (!dist.id && existing.id)) {
                    distributionMap.set(apiServiceName, {
                      service_name: apiServiceName,
                      auto_publish: dist.auto_publish,
                      subtitle_langs: dist.subtitle_langs || [],
                    });
                  }
                });

              return Array.from(distributionMap.values()).map(({ id, ...rest }) => rest);
            })()
          : [],

        video_asset_id: linkedVideoAsset?.id || formData.video_asset_id,
      };

      let episodeId: number;
      if (id && id !== 'new') {
        await episodeService.update(Number(id), form, episodeData);
        episodeId = Number(id);
        message.success('Episode updated successfully');
      } else {
        const created = await episodeService.create(form, episodeData);
        episodeId = created.id;
        message.success('Episode created successfully');
      }

      // Update or create content video assets based on linkedVideoAssetsData
      // 변경사항이 있는 경우에만 API 호출
      if (episodeId) {
        try {
          const linkedVideoIds = new Set(linkedVideoAssetsData.map(a => a.video).filter(Boolean));

          // 1. 삭제할 에셋: 기존에 있었지만 현재 linked 목록에 없는 것
          const assetsToDelete = existingContentVideoAssets.filter(
            a => a.id && a.video && !linkedVideoIds.has(a.video)
          );

          // 2. 새로 생성할 에셋: id가 없는 것 (Link 버튼으로 새로 추가된 것)
          const assetsToCreate = linkedVideoAssetsData.filter(a => !a.id && a.video);

          // 3. 업데이트할 에셋: id가 있고 실제 데이터가 변경된 것만
          const assetsToUpdate = linkedVideoAssetsData.filter(asset => {
            if (!asset.id || !asset.video) return false;
            const existing = existingContentVideoAssets.find(e => e.id === asset.id);
            if (!existing) return false;

            // 실제로 변경된 필드가 있는지 비교
            return (
              (asset.primary ?? false) !== (existing.primary ?? false) ||
              (asset.is_default ?? true) !== (existing.is_default ?? true) ||
              (asset.is_subtitles_embedded ?? false) !==
                (existing.is_subtitles_embedded ?? false) ||
              (asset.after_add_subtitle ?? false) !== (existing.after_add_subtitle ?? false) ||
              (asset.service_name || '') !== (existing.service_name || '') ||
              JSON.stringify(asset.subtitle_ids || null) !==
                JSON.stringify(existing.subtitle_ids || null)
            );
          });

          const hasChanges =
            assetsToDelete.length > 0 || assetsToCreate.length > 0 || assetsToUpdate.length > 0;

          if (hasChanges) {
            const promises: Promise<any>[] = [];

            // 삭제 API 호출
            assetsToDelete.forEach(a => {
              if (a.id) {
                promises.push(contentAssetService.deleteContentVideoAsset(a.id));
              }
            });

            // 생성 API 호출 (새로 Link된 에셋만)
            assetsToCreate.forEach(asset => {
              if (!asset.video) return;
              const videoAssetSet: VideoAssetSet = {
                content: episodeId,
                video: asset.video,
                subtitle_ids: asset.subtitle_ids || null,
                is_subtitles_embedded: asset.is_subtitles_embedded || false,
                is_default: asset.is_default !== undefined ? asset.is_default : true,
                primary: asset.primary !== undefined ? asset.primary : false,
                service_name: asset.service_name || '',
                stream_manifests: asset.stream_manifests || null,
                after_add_subtitle: asset.after_add_subtitle || false,
              };
              promises.push(contentAssetService.saveContentVideoAssets(episodeId, [videoAssetSet]));
            });

            // 업데이트 API 호출 (데이터가 변경된 기존 에셋만)
            assetsToUpdate.forEach(asset => {
              if (!asset.id) return;
              promises.push(
                contentAssetService.updateContentVideoAsset(asset.id, {
                  video: asset.video,
                  subtitle_ids: asset.subtitle_ids || null,
                  is_subtitles_embedded: asset.is_subtitles_embedded || false,
                  is_default: asset.is_default !== undefined ? asset.is_default : true,
                  primary: asset.primary !== undefined ? asset.primary : false,
                  service_name: asset.service_name || '',
                  stream_manifests: asset.stream_manifests || null,
                  after_add_subtitle: asset.after_add_subtitle || false,
                })
              );
            });

            await Promise.all(promises);

            // 새 에셋이 생성된 경우, 서버에서 생성된 에셋들의 primary 동기화가 필요할 수 있음
            if (assetsToCreate.length > 0) {
              const updatedContentAssets =
                await contentAssetService.fetchContentVideoAssets(episodeId);
              setExistingContentVideoAssets(updatedContentAssets);

              const primaryVideoId = linkedVideoAssetsData.find(a => a.primary)?.video;
              if (primaryVideoId) {
                // 새로 생성된 에셋 중 primary 상태가 올바르지 않은 것만 업데이트
                const needsPrimarySync = updatedContentAssets.filter(a => {
                  if (!a.id) return false;
                  const shouldBePrimary = a.video === primaryVideoId;
                  return (a.primary ?? false) !== shouldBePrimary;
                });

                if (needsPrimarySync.length > 0) {
                  await Promise.all(
                    needsPrimarySync.map(a =>
                      contentAssetService.updateContentVideoAsset(a.id!, {
                        primary: a.video === primaryVideoId,
                      })
                    )
                  );
                }
              }
            }

            message.success('Content video assets saved successfully');
          }
          // 변경사항이 없으면 API 호출 없이 스킵
        } catch (error) {
          console.error('Failed to save content video assets:', error);
          message.warning('Episode saved but failed to save content video assets');
        }
      }

      // 비디오 에셋 저장 후 에피소드 정보 다시 로드 (새로 생성된 에셋의 id를 받기 위해)
      if (id && id !== 'new') {
        window.location.reload();
      } else {
        window.location.href = `/episode/edit/${episodeId}`;
      }
    } catch (error: any) {
      console.error('Failed to save episode:', error);
      message.error(error?.response?.data?.detail || 'Failed to save episode');
    } finally {
      // Save 완료 후 세션 ID 정리
      clearActionSessionId();
    }
  };

  // Video Asset columns
  const videoAssetColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      render: (id: number) => {
        return (
          <a
            href={`/assets/edit/${id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              window.open(`/assets/edit/${id}`, '_blank');
            }}
          >
            {id}
          </a>
        );
      },
    },
    {
      title: 'Filename',
      dataIndex: 'file_name',
      key: 'file_name',
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      sorter: true,
      render: (duration: number | undefined) => {
        if (!duration) return '-';
        return formatDuration(duration);
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          Complete: { color: 'green', text: 'Complete' },
          Processing: { color: 'blue', text: 'Processing' },
          Transcoding: { color: 'blue', text: 'Transcoding' },
          Failed: { color: 'red', text: 'Failed' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Ingest Source',
      dataIndex: 'ingest_source',
      key: 'ingest_source',
      render: (ingestSource: string | undefined) => {
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
          'in-house': 'green',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
  ];

  // Stream manifest 상태 표시 헬퍼
  const getManifestStatusDisplay = (status: string) => {
    const statusValue = status.toLowerCase();
    switch (statusValue) {
      case 'completed':
        return { color: 'green', label: 'Completed' };
      case 'failed':
        return { color: 'red', label: 'Failed' };
      case 'in_progress':
      case 'processing':
        return { color: 'orange', label: 'In Progress' };
      case 'transcoding':
        return { color: 'orange', label: 'Transcoding' };
      case 'creating':
        return { color: 'blue', label: 'Creating' };
      case 'start':
        return { color: 'default', label: 'Start' };
      case 'uploading':
        return { color: 'blue', label: 'Uploading' };
      case 'empty':
        return { color: 'default', label: 'Waiting' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };

  // 전체 manifest 완료 상태 계산
  const getManifestOverallStatus = (assets: VideoAssetSet[]) => {
    const allManifests = assets.flatMap(a => a.stream_manifests || []);
    if (allManifests.length === 0) return { color: 'default', label: 'No Manifests' };
    const allCompleted = allManifests.every(m => m.status.toLowerCase() === 'completed');
    const hasFailed = allManifests.some(m => m.status.toLowerCase() === 'failed');
    if (allCompleted) return { color: 'green', label: 'All Completed' };
    if (hasFailed) return { color: 'red', label: 'Has Failed' };
    return { color: 'orange', label: 'In Progress' };
  };

  // 서비스별 manifest 완료 상태 계산
  const getServiceManifestStatus = (manifests: StreamManifest[]) => {
    if (manifests.length === 0) return { color: 'default', label: 'No Manifests' };
    const allCompleted = manifests.every(m => m.status.toLowerCase() === 'completed');
    const hasFailed = manifests.some(m => m.status.toLowerCase() === 'failed');
    if (allCompleted) return { color: 'green', label: 'Completed' };
    if (hasFailed) return { color: 'red', label: 'Has Failed' };
    return { color: 'orange', label: 'In Progress' };
  };

  // video_id 기준으로 그룹화된 데이터 생성
  const getGroupedVideoAssets = () => {
    const videoGroups = new Map<
      number,
      {
        video: number;
        videoData?: VideoType;
        primary: boolean;
        assets: (VideoAssetSet & { videoData?: VideoType })[];
      }
    >();

    linkedVideoAssetsData.forEach(asset => {
      if (asset.video) {
        if (!videoGroups.has(asset.video)) {
          videoGroups.set(asset.video, {
            video: asset.video,
            videoData: asset.videoData,
            primary: asset.primary || false,
            assets: [],
          });
        }
        videoGroups.get(asset.video)!.assets.push(asset);
        // primary가 true인 에셋이 있으면 그룹의 primary도 true로 설정
        if (asset.primary) {
          videoGroups.get(asset.video)!.primary = true;
        }
      }
    });

    return Array.from(videoGroups.values());
  };

  // Linked Video Asset columns (video_id 기준으로 그룹화된 데이터용)
  const linkedVideoAssetColumns = [
    {
      title: 'Video ID',
      dataIndex: 'video',
      key: 'video',
      render: (videoId: number) => {
        return (
          <a
            href={`/assets/edit/${videoId}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => {
              e.preventDefault();
              window.open(`/assets/edit/${videoId}`, '_blank');
            }}
          >
            {videoId}
          </a>
        );
      },
    },
    {
      title: 'Filename',
      dataIndex: 'videoData',
      key: 'file_name',
      render: (_: any, record: { videoData?: VideoType }) => {
        return record.videoData?.file_name || '-';
      },
    },
    {
      title: 'Duration',
      dataIndex: 'videoData',
      key: 'duration',
      render: (_: any, record: { videoData?: VideoType }) => {
        const duration = record.videoData?.duration;
        if (!duration) return '-';
        return formatDuration(duration);
      },
    },
    {
      title: 'Status',
      dataIndex: 'videoData',
      key: 'status',
      render: (_: any, record: { videoData?: VideoType }) => {
        const status = record.videoData?.status || '';
        const statusMap: Record<string, { color: string; text: string }> = {
          Complete: { color: 'green', text: 'Complete' },
          Processing: { color: 'blue', text: 'Processing' },
          Transcoding: { color: 'blue', text: 'Transcoding' },
          Failed: { color: 'red', text: 'Failed' },
        };
        const statusInfo = statusMap[status] || { color: 'default', text: status };
        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
      },
    },
    {
      title: 'Ingest Source',
      dataIndex: 'videoData',
      key: 'ingest_source',
      render: (_: any, record: { videoData?: VideoType }) => {
        const ingestSource = record.videoData?.ingest_source;
        if (!ingestSource) {
          return <span style={{ color: '#999' }}>-</span>;
        }
        const colorMap: Record<string, string> = {
          mediahold: 'blue',
          kcp: 'purple',
          'in-house': 'green',
        };
        return (
          <Tag color={colorMap[ingestSource.toLowerCase()] || 'default'}>
            {ingestSource.toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: 'Primary',
      dataIndex: 'primary',
      key: 'primary',
      render: (_: any, record: { video: number; primary: boolean }) => {
        return (
          <Checkbox
            checked={record.primary || false}
            onChange={e => handlePrimaryChange(record.video, e.target.checked)}
          />
        );
      },
    },
  ];

  // Distribution admin 링크 열기
  const handleOpenDistributionAdmin = (
    _contentId: number,
    episodeId: number,
    serviceName: string
  ) => {
    const hostMap: Record<string, string> = {
      'odc-staging': 'https://admin.staging2.odkmedia.net/odc',
      odc: 'https://admin.odkmedia.net/odc',
      'odk-staging': 'https://admin.staging2.odkmedia.net/odk',
      odk: 'https://admin.odkmedia.net/odk',
      'amasian-staging': 'https://amasian-admin.staging2.odkmedia.net',
      amasian: 'https://amasian-admin.odkmedia.net',
    };

    if (serviceName === 'amasian' || serviceName === 'amasian-staging') {
      const url = `${hostMap[serviceName]}/video/detail/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odc' || serviceName === 'odc-staging') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else if (serviceName === 'odk' || serviceName === 'odk-staging') {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    } else {
      const url = `${hostMap[serviceName]}/content/episode/${episodeId}`;
      window.open(url, '_blank');
    }
  };

  return (
    <Component.BasePageComponent>
      <div style={{ padding: '24px' }}>
        <Form form={form} layout="vertical" className="episode-edit-form">
          {/* Admin Links */}
          <div className="admin-links-container">
            <div
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#fffbe6',
                borderRadius: '4px',
                marginBottom: '16px',
              }}
            >
              <Space>
                {episode?.distribution_sets && episode.distribution_sets.length > 0 ? (
                  episode.distribution_sets.map((dist, index) => (
                    <Button
                      key={index}
                      size="small"
                      onClick={() =>
                        handleOpenDistributionAdmin(
                          dist.content_id,
                          dist.episode_id,
                          dist.service_name
                        )
                      }
                    >
                      {dist.service_name}
                    </Button>
                  ))
                ) : (
                  <div style={{ color: '#999' }}>No Distribution</div>
                )}
              </Space>
            </div>
          </div>

          {/* Episode Metadata Section */}
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span>Episode Metadata</span>
                {id && id !== 'new' && (
                  <Button
                    size="small"
                    type="link"
                    icon={<HistoryOutlined />}
                    onClick={handleViewActionHistory}
                    style={{ fontSize: '12px', padding: '0 4px' }}
                  >
                    View Action History
                  </Button>
                )}
              </div>
            }
            style={{ marginBottom: '24px' }}
          >
            <Form.Item label="Episode Type" name="episode_type" initialValue="tv-series">
              <Select>
                <Select.Option value="tv-series">tv-series (default)</Select.Option>
                <Select.Option value="clip">clip</Select.Option>
                <Select.Option value="trailer">trailer</Select.Option>
                <Select.Option value="additional">additional</Select.Option>
                <Select.Option value="live_streaming">live-streaming</Select.Option>
              </Select>
            </Form.Item>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label={
                  <div>
                    Series Slug{' '}
                    <Button
                      type="link"
                      onClick={() => window.open(`/series/edit2/${series?.id}`, '_blank')}
                    >
                      Open Link
                    </Button>
                  </div>
                }
                name="series_slug"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input disabled value={series?.slug} />
              </Form.Item>
              <Form.Item
                label={
                  <div>
                    Season Slug{' '}
                    {season?.id ? (
                      <Button
                        type="link"
                        onClick={() => window.open(`/season/edit2/${season.id}`, '_blank')}
                      >
                        Open Link
                      </Button>
                    ) : (
                      <Button type="link" onClick={() => setSeasonSearchModalVisible(true)}>
                        Select Season
                      </Button>
                    )}
                  </div>
                }
                name="season_slug"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input disabled value={season?.slug || ''} />
              </Form.Item>
            </div>

            {/* Episode Details */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <Form.Item
                label="Episode Number (default=largest ep# +1)*"
                name="episode_order"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: 'Please enter episode number' }]}
              >
                <Input
                  type="number"
                  min={0}
                  onChange={e => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    handleEpisodeNumberChange(isNaN(value as number) ? null : value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Episode Part Number"
                name="episode_part_number"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Input
                  type="number"
                  min={0}
                  onChange={e => {
                    const value = e.target.value === '' ? null : Number(e.target.value);
                    handleEpisodePartNumberChange(isNaN(value as number) ? null : value);
                  }}
                />
              </Form.Item>
              <Form.Item
                label="Episode Slug (auto-gen from episode number)"
                name="slug"
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: 'Please enter a slug' }]}
              >
                <Input />
              </Form.Item>
            </div>

            {/* Episode Titles (Primary) */}
            <div style={{ marginBottom: '16px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Episode Title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        handleTitleChange({
                          ...i18nTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            {/* Secondary Episode Titles */}
            <div style={{ marginBottom: '16px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `Secondary Episode Title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSecondaryTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSecondaryTitle({
                          ...i18nSecondaryTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                    />
                  ),
                }))}
              />
            </div>

            {/* On-Air-Date */}
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <Form.Item label="On-Air-Date" name="on_air_date">
                  <DatePicker />
                </Form.Item>
              </div>
            </div>
          </Card>

          {/* SEO Fields Section */}
          <Card title="SEO Information" style={{ marginBottom: '24px' }}>
            {/* SEO Title */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO title ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoTitle as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoTitle({
                          ...i18nSeoTitle,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            <Divider />

            {/* SEO Keyword */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO keyword ${lang.rfc_code}`,
                  children: (
                    <Input
                      value={(i18nSeoKeyword as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoKeyword({
                          ...i18nSeoKeyword,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>

            <Divider />

            {/* SEO Synopsis */}
            <div style={{ marginBottom: '24px' }}>
              <Tabs
                type="card"
                items={writeLanguages.map(lang => ({
                  key: lang.rfc_code.toLowerCase(),
                  label: `SEO Synopsis ${lang.rfc_code}`,
                  children: (
                    <Input.TextArea
                      rows={6}
                      value={(i18nSeoSynopsis as any)[lang.rfc_code.toLowerCase()] || ''}
                      onChange={e => {
                        setI18nSeoSynopsis({
                          ...i18nSeoSynopsis,
                          [lang.rfc_code.toLowerCase()]: e.target.value,
                        } as I18nFieldData);
                      }}
                      placeholder={lang.rfc_code.toLowerCase() === 'en' ? 'En required *' : ''}
                    />
                  ),
                }))}
              />
            </div>
          </Card>

          {/* Video Assets Section */}
          <Card title="Video Assets" style={{ marginBottom: '24px' }}>
            <Space style={{ marginBottom: '16px' }}>
              <Input
                placeholder="keyword"
                value={videoAssetKeyword}
                onChange={e => setVideoAssetKeyword(e.target.value)}
                onPressEnter={handleVideoAssetSearch}
                style={{ width: '200px' }}
              />
              <Button onClick={handleVideoAssetSearch} loading={isLoadingVideoAssets}>
                Search
              </Button>
            </Space>

            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              {/* Video Asset Search Table */}
              <div style={{ flex: 1 }}>
                <Spin spinning={isLoadingVideoAssets}>
                  <Table
                    columns={videoAssetColumns}
                    dataSource={videoAssets}
                    rowKey="id"
                    rowSelection={{
                      type: 'radio',
                      selectedRowKeys: selectedVideoAssets,
                      onChange: (selectedKeys: React.Key[]) => {
                        // radio 타입이므로 항상 1개만 선택됨
                        setSelectedVideoAssets(
                          selectedKeys.length > 0 ? [selectedKeys[0] as number] : []
                        );
                      },
                    }}
                    pagination={{
                      current: videoAssetCurrentPage,
                      total: videoAssetTotal,
                      pageSize: 10,
                      showSizeChanger: false,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    onChange={pagination => {
                      if (pagination.current) {
                        handleVideoAssetPageChange(pagination.current);
                      }
                    }}
                    size="small"
                  />
                </Spin>
              </div>

              {/* Link/Unlink Buttons */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '0 8px',
                }}
              >
                <Button
                  type="primary"
                  onClick={handleLinkVideoAssets}
                  disabled={selectedVideoAssets.length !== 1}
                >
                  Link &gt;
                </Button>
                <Button
                  onClick={handleUnlinkVideoAssets}
                  disabled={selectedVideoAssets.length !== 1 || linkedVideoAssetsData.length === 0}
                >
                  &lt; Unlink
                </Button>
              </div>

              {/* Linked Video Asset Table */}
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {getGroupedVideoAssets().length > 0 ? (
                  <>
                    <Table
                      columns={linkedVideoAssetColumns}
                      dataSource={getGroupedVideoAssets()}
                      rowKey={record => record.video.toString()}
                      pagination={false}
                      size="small"
                      rowSelection={{
                        type: 'radio',
                        selectedRowKeys: selectedVideoAssets
                          .filter(id => getGroupedVideoAssets().some(group => group.video === id))
                          .map(String),
                        onChange: (selectedKeys: React.Key[]) => {
                          // radio 타입이므로 항상 1개만 선택됨
                          if (selectedKeys.length > 0) {
                            const selectedVideoId = Number(selectedKeys[0]);
                            // 그룹화된 데이터에 있는 비디오인지 확인
                            const existsInLinked = getGroupedVideoAssets().some(
                              group => group.video === selectedVideoId
                            );
                            if (existsInLinked) {
                              setSelectedVideoAssets([selectedVideoId]);
                              // 선택된 비디오의 videoData로 linkedVideoAsset 업데이트
                              const selectedGroup = getGroupedVideoAssets().find(
                                g => g.video === selectedVideoId
                              );
                              if (selectedGroup?.videoData) {
                                setLinkedVideoAsset(selectedGroup.videoData);
                                // subtitle 정보 업데이트
                                videoSubtitleService
                                  .fetchByVideoId(selectedVideoId)
                                  .then(subtitles => {
                                    setLinkedVideoAssetSubtitles(subtitles);
                                  })
                                  .catch(error => {
                                    console.error(
                                      `Failed to fetch subtitles for video ${selectedVideoId}:`,
                                      error
                                    );
                                    setLinkedVideoAssetSubtitles([]);
                                  });
                              }
                            }
                          } else {
                            setSelectedVideoAssets([]);
                          }
                        },
                      }}
                    />
                    {hasPlayableVideoUrl(linkedVideoAsset) ? (
                      <div
                        style={{
                          marginTop: '16px',
                          width: '100%',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                        }}
                      >
                        <div
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#fffbe6',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          Preview Video
                        </div>
                        <div
                          style={{
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            boxSizing: 'border-box',
                          }}
                        >
                          <Preview
                            videoUrl={getPlayableVideoUrl(linkedVideoAsset) || ''}
                            subtitles={linkedVideoAssetSubtitles}
                            thumbnailUrl={
                              linkedVideoAsset?.video_thumbnails?.find(t => t.is_default)?.url ||
                              linkedVideoAsset?.video_thumbnails?.[0]?.url ||
                              undefined
                            }
                          />
                        </div>
                      </div>
                    ) : (
                      <div style={{ marginTop: '16px' }}>
                        <div
                          style={{
                            padding: '8px 16px',
                            backgroundColor: '#fffbe6',
                            borderRadius: '4px',
                            marginBottom: '8px',
                          }}
                        >
                          No Preview Video
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No linked video asset
                  </div>
                )}
              </div>
            </div>
            {/* Content Stream Manifests Section */}
            {(() => {
              const selectedVideoId =
                selectedVideoAssets.length > 0 ? selectedVideoAssets[0] : null;
              const selectedGroup = selectedVideoId
                ? getGroupedVideoAssets().find(g => g.video === selectedVideoId)
                : getGroupedVideoAssets()[0];
              const serviceAssets = selectedGroup?.assets || [];
              const hasAnyManifests = serviceAssets.some(
                a => a.stream_manifests && a.stream_manifests.length > 0
              );

              if (!hasAnyManifests) return null;

              const overallStatus = getManifestOverallStatus(serviceAssets);

              return (
                <div
                  style={{
                    marginTop: '16px',
                    border: '1px solid #f0f0f0',
                    borderRadius: '8px',
                    padding: '16px',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '12px',
                    }}
                  >
                    <GlobalOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <Text strong>Content Stream Manifests</Text>
                    <Tag color={overallStatus.color} style={{ marginLeft: '12px' }}>
                      {overallStatus.label}
                    </Tag>
                  </div>
                  <Collapse
                    size="small"
                    defaultActiveKey={[]}
                    items={serviceAssets
                      .filter(asset => asset.stream_manifests && asset.stream_manifests.length > 0)
                      .map(asset => {
                        const manifests = asset.stream_manifests || [];
                        const serviceStatus = getServiceManifestStatus(manifests);
                        return {
                          key: asset.service_name,
                          label: (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Text strong>{asset.service_name}</Text>
                              <Tag color="blue" style={{ fontSize: '11px' }}>
                                {manifests.length} manifests
                              </Tag>
                              <Tag color={serviceStatus.color} style={{ fontSize: '11px' }}>
                                {serviceStatus.label}
                              </Tag>
                            </div>
                          ),
                          children: (
                            <Row gutter={[8, 8]}>
                              {manifests.map((manifest, idx) => {
                                const statusDisplay = getManifestStatusDisplay(manifest.status);
                                const hlsUrl = manifest.stream_url_full || manifest.stream_url;
                                const isCompleted = manifest.status.toLowerCase() === 'completed';
                                const isFailed = manifest.status.toLowerCase() === 'failed';
                                const isInProgress = [
                                  'in_progress',
                                  'processing',
                                  'creating',
                                  'start',
                                  'transcoding',
                                  'uploading',
                                ].includes(manifest.status.toLowerCase());

                                return (
                                  <Col xs={24} sm={12} lg={8} key={idx}>
                                    <Card
                                      size="small"
                                      style={{
                                        borderRadius: '6px',
                                        border: `1px solid ${
                                          statusDisplay.color === 'green'
                                            ? '#b7eb8f'
                                            : statusDisplay.color === 'red'
                                              ? '#ffa39e'
                                              : statusDisplay.color === 'orange'
                                                ? '#ffe58f'
                                                : '#d9d9d9'
                                        }`,
                                      }}
                                      bodyStyle={{ padding: '12px' }}
                                    >
                                      <div
                                        style={{
                                          textAlign: 'center',
                                          marginBottom: '8px',
                                        }}
                                      >
                                        <Text strong style={{ fontSize: '13px' }}>
                                          {manifest.type?.toUpperCase() || 'Stream'}
                                        </Text>
                                        <br />
                                        <Text type="secondary" style={{ fontSize: '11px' }}>
                                          {manifest.preset}
                                        </Text>
                                      </div>
                                      <div
                                        style={{
                                          textAlign: 'center',
                                          marginBottom: '8px',
                                        }}
                                      >
                                        <Tag
                                          color={statusDisplay.color}
                                          style={{ fontSize: '10px' }}
                                        >
                                          {statusDisplay.label}
                                        </Tag>
                                      </div>
                                      {isCompleted && hlsUrl && (
                                        <div
                                          style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                          }}
                                        >
                                          <ManifestSubtitleTags url={hlsUrl} />
                                        </div>
                                      )}
                                      {isCompleted && hlsUrl && (
                                        <div style={{ textAlign: 'center' }}>
                                          <Text
                                            style={{
                                              fontSize: '9px',
                                              color: '#999',
                                              wordBreak: 'break-all',
                                              display: 'block',
                                              marginBottom: '4px',
                                            }}
                                          >
                                            {hlsUrl}
                                          </Text>
                                          <Button
                                            size="small"
                                            type="link"
                                            style={{
                                              fontSize: '11px',
                                              padding: 0,
                                            }}
                                            onClick={() => {
                                              navigator.clipboard.writeText(hlsUrl);
                                              message.success('URL copied');
                                            }}
                                          >
                                            Copy URL
                                          </Button>
                                        </div>
                                      )}
                                      {isCompleted && hlsUrl && (
                                        <div style={{ marginTop: '12px' }}>
                                          <Preview videoUrl={hlsUrl} />
                                        </div>
                                      )}
                                      {isInProgress && (
                                        <div
                                          style={{
                                            textAlign: 'center',
                                            padding: '8px 0',
                                          }}
                                        >
                                          <Spin size="small" />
                                        </div>
                                      )}
                                      {isFailed && (
                                        <div
                                          style={{
                                            textAlign: 'center',
                                            padding: '4px 0',
                                          }}
                                        >
                                          <Text type="danger" style={{ fontSize: '11px' }}>
                                            Processing Failed
                                          </Text>
                                        </div>
                                      )}
                                    </Card>
                                  </Col>
                                );
                              })}
                            </Row>
                          ),
                        };
                      })}
                  />
                </div>
              );
            })()}
          </Card>

          {/* Service Auto Distribution Section */}
          <Card title="Service Auto Distribution" style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
              <Checkbox checked={inherit} onChange={e => setInherit(e.target.checked)}>
                <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Inherit from Season</span>
              </Checkbox>
            </div>

            {!inherit ? (
              <>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Service Auto Distribution for the episode.
                </p>

                <Table
                  dataSource={(() => {
                    const data: any[] = [];

                    if (process.env.REACT_APP_ENV === 'production') {
                      // Production 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = distributions.find(
                          d => d.service_name === service.name && d.environment === 'production'
                        );
                        data.push({
                          key: `${service.name}-production`,
                          service: service.name,
                          environment: 'production',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'production',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    } else {
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = distributions.find(
                          d => d.service_name === service.name && d.environment === 'staging'
                        );
                        data.push({
                          key: `${service.name}-staging`,
                          service: service.name,
                          environment: 'staging',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'staging',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    }

                    // // Divider 추가
                    // data.push({
                    //   key: 'divider',
                    //   isDivider: true,
                    // });

                    return data;
                  })()}
                  columns={[
                    {
                      title: 'Service',
                      dataIndex: 'service',
                      key: 'service',
                      width: '320px',
                      render: (text: string, record: any) => {
                        if (record.isDivider) {
                          return {
                            children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                            props: { colSpan: 3 },
                          };
                        }
                        return (
                          <Checkbox
                            checked={record.dist.enabled || false}
                            onChange={e => {
                              updateDistribution(record.service, record.environment, {
                                enabled: e.target.checked,
                              });
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {text} (
                              {record.environment === 'production' ? 'Production' : 'Staging'})
                            </span>
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Auto Publish',
                      key: 'auto_publish',
                      width: '150px',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Checkbox
                            checked={record.dist.auto_publish || false}
                            disabled={!record.dist.enabled}
                            onChange={e =>
                              updateDistribution(record.service, record.environment, {
                                auto_publish: e.target.checked,
                              })
                            }
                          >
                            Auto Publish
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Subtitles',
                      key: 'subtitles',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Space wrap>
                            {SUBTITLE_LANGUAGES.map(lang => (
                              <Checkbox
                                key={lang.value}
                                checked={record.dist.subtitle_langs?.includes(lang.value) || false}
                                disabled={!record.dist.enabled}
                                onChange={e => {
                                  const newSubtitles = e.target.checked
                                    ? [...(record.dist.subtitle_langs || []), lang.value]
                                    : (record.dist.subtitle_langs || []).filter(
                                        (s: string) => s !== lang.value
                                      );
                                  updateDistribution(record.service, record.environment, {
                                    subtitle_langs: newSubtitles,
                                  });
                                }}
                              >
                                {lang.label}
                              </Checkbox>
                            ))}
                          </Space>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  bordered
                />
              </>
            ) : (
              <>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                  {season && (season as any).service_auto_distribution_inherit !== false
                    ? 'Inherit Service Auto Distribution from Season (which inherits from Series).'
                    : 'Inherit Service Auto Distribution from Season.'}
                </p>
                <Table
                  dataSource={(() => {
                    const data: any[] = [];
                    // Divider 추가
                    data.push({
                      key: 'divider',
                      isDivider: true,
                    });

                    if (process.env.REACT_APP_ENV === 'production') {
                      // Production 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = displayDistributions.find(
                          d => d.service_name === service.name && d.environment === 'production'
                        );
                        data.push({
                          key: `${service.name}-production`,
                          service: service.name,
                          environment: 'production',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'production',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    } else {
                      // Staging 서비스 추가
                      SERVICE_OPTIONS.forEach(service => {
                        const dist = displayDistributions.find(
                          d => d.service_name === service.name && d.environment === 'staging'
                        );
                        data.push({
                          key: `${service.name}-staging`,
                          service: service.name,
                          environment: 'staging',
                          dist: dist || {
                            service_name: service.name,
                            environment: 'staging',
                            enabled: false,
                            auto_publish: false,
                            subtitle_langs: [],
                          },
                        });
                      });
                    }

                    return data;
                  })()}
                  columns={[
                    {
                      title: 'Service',
                      dataIndex: 'service',
                      key: 'service',
                      width: '320px',
                      render: (text: string, record: any) => {
                        if (record.isDivider) {
                          return {
                            children: <div style={{ height: '1px', background: '#e8e8e8' }} />,
                            props: { colSpan: 3 },
                          };
                        }
                        return (
                          <Checkbox
                            disabled={true}
                            checked={record.dist.enabled || false}
                            onChange={e => {
                              updateDistribution(record.service, record.environment, {
                                enabled: e.target.checked,
                              });
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {text} (
                              {record.environment === 'production' ? 'Production' : 'Staging'})
                            </span>
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Auto Publish',
                      key: 'auto_publish',
                      width: '150px',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Checkbox
                            disabled={true}
                            checked={record.dist.auto_publish || false}
                            onChange={e =>
                              updateDistribution(record.service, record.environment, {
                                auto_publish: e.target.checked,
                              })
                            }
                          >
                            Auto Publish
                          </Checkbox>
                        );
                      },
                    },
                    {
                      title: 'Subtitles',
                      key: 'subtitles',
                      render: (_: any, record: any) => {
                        if (record.isDivider) {
                          return { props: { colSpan: 0 } };
                        }
                        return (
                          <Space wrap>
                            {SUBTITLE_LANGUAGES.map(lang => (
                              <Checkbox
                                key={lang.value}
                                checked={record.dist.subtitle_langs?.includes(lang.value) || false}
                                disabled={true}
                                onChange={e => {
                                  const newSubtitles = e.target.checked
                                    ? [...(record.dist.subtitle_langs || []), lang.value]
                                    : (record.dist.subtitle_langs || []).filter(
                                        (s: string) => s !== lang.value
                                      );
                                  updateDistribution(record.service, record.environment, {
                                    subtitle_langs: newSubtitles,
                                  });
                                }}
                              >
                                {lang.label}
                              </Checkbox>
                            ))}
                          </Space>
                        );
                      },
                    },
                  ]}
                  pagination={false}
                  bordered
                />
              </>
            )}
          </Card>

          {/* Thumbnail Override Section - 기존 에피소드만 표시 */}
          {id && (
            <Row gutter={[24, 24]} style={{ marginTop: '24px' }}>
              <Col xs={24} lg={16}>
                <Card
                  title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <FileTextOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                      <span>Thumbnail Override</span>
                    </div>
                  }
                >
                  <p style={{ marginBottom: '16px', color: '#666' }}>
                    서비스별로 다른 썸네일을 설정할 수 있습니다. Override가 없으면 Video의 기본
                    썸네일이 사용됩니다.
                  </p>
                  <Row gutter={[16, 16]}>
                    {THUMBNAIL_OVERRIDE_LANGUAGES.map(lang => {
                      const existingOverride = thumbnailOverrides.get(lang.value);
                      const uploadedImage = uploadedThumbnails.get(lang.value);
                      const displayImage = uploadedImage || existingOverride?.url || null;

                      // Video의 해당 언어 썸네일 찾기
                      const videoLangThumbnail = linkedVideoAsset?.video_thumbnails?.find(
                        t => t.language?.toUpperCase() === lang.value.toUpperCase() && !t.is_default
                      );
                      const videoDefaultThumbnail = linkedVideoAsset?.video_thumbnails?.find(
                        t => t.is_default
                      );

                      return (
                        <Col xs={24} sm={12} md={6} key={lang.value}>
                          <div
                            style={{
                              border: '1px solid #d9d9d9',
                              borderRadius: '8px',
                              padding: '16px',
                              textAlign: 'center',
                            }}
                          >
                            <div
                              style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 'bold' }}
                            >
                              {lang.flag} {lang.label}
                            </div>
                            <div style={{ marginBottom: '4px', fontSize: '12px', color: '#888' }}>
                              ({lang.service})
                            </div>
                            {/* Video Thumbnail 상태 뱃지 */}
                            {linkedVideoAsset && (
                              <div style={{ marginBottom: '8px' }}>
                                {videoLangThumbnail ? (
                                  <Popover
                                    content={
                                      <div style={{ textAlign: 'center', maxWidth: 280 }}>
                                        <img
                                          src={videoLangThumbnail.url}
                                          alt={`Video ${lang.label} thumbnail`}
                                          style={{
                                            maxWidth: '100%',
                                            maxHeight: 160,
                                            borderRadius: 4,
                                            objectFit: 'contain',
                                          }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                                          Video Thumbnail ({lang.label})
                                          <br />
                                          {videoLangThumbnail.width} x {videoLangThumbnail.height}
                                        </div>
                                      </div>
                                    }
                                    title="Video Thumbnail"
                                    trigger="hover"
                                  >
                                    <Tag
                                      color="green"
                                      style={{ cursor: 'pointer', fontSize: '11px' }}
                                    >
                                      <VideoCameraOutlined /> Video ✓
                                    </Tag>
                                  </Popover>
                                ) : videoDefaultThumbnail ? (
                                  <Popover
                                    content={
                                      <div style={{ textAlign: 'center', maxWidth: 280 }}>
                                        <img
                                          src={videoDefaultThumbnail.url}
                                          alt="Video default thumbnail"
                                          style={{
                                            maxWidth: '100%',
                                            maxHeight: 160,
                                            borderRadius: 4,
                                            objectFit: 'contain',
                                          }}
                                        />
                                        <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                                          Default Thumbnail (언어별 미설정)
                                          <br />
                                          {videoDefaultThumbnail.width} x{' '}
                                          {videoDefaultThumbnail.height}
                                        </div>
                                      </div>
                                    }
                                    title="Video Default Thumbnail"
                                    trigger="hover"
                                  >
                                    <Tag
                                      color="default"
                                      style={{ cursor: 'pointer', fontSize: '11px' }}
                                    >
                                      <VideoCameraOutlined /> Default Only
                                    </Tag>
                                  </Popover>
                                ) : (
                                  <Tag color="default" style={{ fontSize: '11px', opacity: 0.6 }}>
                                    <VideoCameraOutlined /> Video -
                                  </Tag>
                                )}
                              </div>
                            )}
                            <Upload
                              accept="image/*"
                              showUploadList={false}
                              beforeUpload={file => {
                                handleThumbnailUpload(lang.value, file);
                                return false;
                              }}
                            >
                              <div
                                style={{
                                  border: displayImage ? '2px solid #1890ff' : '2px dashed #d9d9d9',
                                  borderRadius: '8px',
                                  minHeight: '120px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: displayImage ? '#f6f8fa' : '#fafafa',
                                  marginBottom: '12px',
                                  position: 'relative',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  overflow: 'hidden',
                                }}
                                onMouseEnter={e => {
                                  e.currentTarget.style.borderColor = '#1890ff';
                                  e.currentTarget.style.backgroundColor = displayImage
                                    ? '#e6f7ff'
                                    : '#f0f0f0';
                                  e.currentTarget.style.transform = 'translateY(-2px)';
                                  e.currentTarget.style.boxShadow =
                                    '0 4px 12px rgba(24, 144, 255, 0.15)';
                                  const hint = e.currentTarget.querySelector(
                                    '.upload-hint'
                                  ) as HTMLElement;
                                  if (hint) {
                                    hint.style.opacity = '1';
                                  }
                                }}
                                onMouseLeave={e => {
                                  e.currentTarget.style.borderColor = displayImage
                                    ? '#1890ff'
                                    : '#d9d9d9';
                                  e.currentTarget.style.backgroundColor = displayImage
                                    ? '#f6f8fa'
                                    : '#fafafa';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                  e.currentTarget.style.boxShadow = 'none';
                                  const hint = e.currentTarget.querySelector(
                                    '.upload-hint'
                                  ) as HTMLElement;
                                  if (hint) {
                                    hint.style.opacity = '0';
                                  }
                                }}
                              >
                                {displayImage ? (
                                  <>
                                    <img
                                      src={displayImage}
                                      alt={`${lang.label} thumbnail`}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '100px',
                                        borderRadius: '4px',
                                        objectFit: 'contain',
                                      }}
                                    />
                                    <div
                                      className="upload-hint"
                                      style={{
                                        position: 'absolute',
                                        top: '6px',
                                        right: '6px',
                                        backgroundColor: 'rgba(24, 144, 255, 0.95)',
                                        color: 'white',
                                        borderRadius: '6px',
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                        pointerEvents: 'none',
                                      }}
                                    >
                                      Click to replace
                                    </div>
                                    {existingOverride && (
                                      <Tag
                                        color="green"
                                        style={{
                                          position: 'absolute',
                                          bottom: '6px',
                                          left: '6px',
                                          fontSize: '10px',
                                        }}
                                      >
                                        Override
                                      </Tag>
                                    )}
                                  </>
                                ) : (
                                  <div
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '8px',
                                      padding: '16px',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <UploadOutlined
                                        style={{
                                          fontSize: '20px',
                                          color: '#8c8c8c',
                                        }}
                                      />
                                    </div>
                                    <div
                                      style={{
                                        fontSize: '12px',
                                        color: '#595959',
                                        fontWeight: 500,
                                        textAlign: 'center',
                                      }}
                                    >
                                      Click to upload
                                      <br />
                                      <span
                                        style={{
                                          fontSize: '10px',
                                          color: '#8c8c8c',
                                          fontWeight: 400,
                                        }}
                                      >
                                        No override
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </Upload>
                            {displayImage && (
                              <div style={{ marginTop: '8px' }}>
                                <Button
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => {
                                    Modal.confirm({
                                      title: 'Delete Thumbnail Override',
                                      content: `Are you sure you want to delete the ${lang.label} thumbnail override?`,
                                      onOk: () => handleDeleteThumbnailOverride(lang.value),
                                    });
                                  }}
                                  block
                                >
                                  Delete
                                </Button>
                              </div>
                            )}
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                  <div style={{ marginTop: '24px', textAlign: 'center' }}>
                    <Button
                      type="primary"
                      size="large"
                      icon={<UploadOutlined />}
                      onClick={handleSaveAllThumbnailOverrides}
                      loading={savingThumbnails}
                      disabled={uploadedThumbnails.size === 0}
                    >
                      Save All Thumbnail Overrides
                    </Button>
                  </div>
                </Card>
              </Col>

              <Col xs={24} lg={8}>
                <Card
                  title="Thumbnail Distribution Rule"
                  style={{
                    backgroundColor: '#fffbe6',
                    border: '1px solid #ffe58f',
                  }}
                >
                  <div style={{ padding: '8px 0' }}>
                    {THUMBNAIL_DISTRIBUTION_RULES.map((rule, index) => (
                      <div
                        key={index}
                        style={{
                          padding: '8px 0',
                          borderBottom:
                            index < THUMBNAIL_DISTRIBUTION_RULES.length - 1
                              ? '1px solid #ffe58f'
                              : 'none',
                        }}
                      >
                        <Text strong>{rule.language}</Text>
                        <Text> → {rule.service}</Text>
                      </div>
                    ))}
                  </div>
                  <Divider style={{ margin: '16px 0' }} />
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <p style={{ marginBottom: '8px' }}>
                      <strong>동작 원리:</strong>
                    </p>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                      <li>Override가 없으면 Video의 기본 썸네일 사용</li>
                      <li>Override가 있으면 Content의 Override 썸네일 우선 사용</li>
                      <li>Distribution 시 서비스별 언어에 맞는 썸네일 전달</li>
                    </ul>
                  </div>
                  {linkedVideoAsset && (
                    <>
                      <Divider style={{ margin: '16px 0' }} />
                      <div style={{ fontSize: '12px' }}>
                        <a
                          href={`/assets/edit/${linkedVideoAsset.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#1890ff' }}
                        >
                          Video Asset 페이지에서 썸네일 수정 →
                        </a>
                      </div>
                    </>
                  )}
                </Card>
              </Col>
            </Row>
          )}

          {/* Save Button */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'right',
              alignItems: 'center',
              gap: '16px',
              marginTop: '32px',
            }}
          >
            {/* <Card>
              <Form.Item label="Episode Status" name="status" style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: '200px' }}
                  value={
                    form.getFieldValue('status') === true ||
                    form.getFieldValue('status') === 'published'
                      ? 'published'
                      : 'unpublished'
                  }
                  onChange={value => {
                    form.setFieldValue('status', value);
                  }}
                >
                  <Select.Option value="published">enabled</Select.Option>
                  <Select.Option value="unpublished">disabled</Select.Option>
                </Select>
              </Form.Item>
            </Card> */}
            <Button type="primary" size="large" onClick={handleSave}>
              Save Episode
            </Button>
          </div>
        </Form>

        {/* Season Search Modal */}
        <Modal
          title="Select Season"
          open={seasonSearchModalVisible}
          onCancel={() => {
            setSeasonSearchModalVisible(false);
            setSeasonSearchKeyword('');
            setSearchSeasons(defaultSeasons);
          }}
          footer={null}
          width={800}
        >
          <div style={{ marginBottom: '16px' }}>
            <Input
              placeholder="Search season by title..."
              value={seasonSearchKeyword}
              onChange={e => handleSeasonSearch(e.target.value)}
              allowClear
              onClear={() => {
                setSeasonSearchKeyword('');
                setSearchSeasons(defaultSeasons);
              }}
            />
          </div>
          <Spin spinning={seasonSearchLoading}>
            <div
              style={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
              }}
            >
              {searchSeasons.length > 0 ? (
                <Table
                  columns={[
                    {
                      title: 'ID',
                      dataIndex: 'id',
                      key: 'id',
                    },
                    {
                      title: 'Title',
                      dataIndex: 'i18n_title',
                      key: 'title',
                      render: (i18nTitle: I18nFieldData | undefined, record: SeasonData) => {
                        return i18nTitle?.en || record.title_en || record.title || '-';
                      },
                    },
                    {
                      title: 'Season Number',
                      dataIndex: 'season_number',
                      key: 'season_number',
                      render: (num: number | undefined, record: SeasonData) => {
                        return num || record.season_order || '-';
                      },
                    },
                    {
                      title: 'Action',
                      key: 'action',
                      render: (_: any, record: SeasonData) => (
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSelectSeason(record)}
                        >
                          Select
                        </Button>
                      ),
                    },
                  ]}
                  dataSource={searchSeasons}
                  rowKey="id"
                  pagination={false}
                  size="small"
                />
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                  {seasonSearchLoading ? 'Searching...' : 'No seasons found'}
                </div>
              )}
            </div>
          </Spin>
        </Modal>
        {/* Action History Modal */}
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <HistoryOutlined style={{ color: '#1890ff' }} />
              <span>Action History</span>
              {!actionHistoryLoading && <Tag color="blue">{actionHistories.length} Actions</Tag>}
            </div>
          }
          open={actionHistoryModalVisible}
          onCancel={() => setActionHistoryModalVisible(false)}
          footer={null}
          width={800}
        >
          <Spin spinning={actionHistoryLoading}>
            <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: '8px' }}>
              {actionHistories.length > 0 ? (
                <Timeline>
                  {actionHistories
                    .sort((a, b) => {
                      if (a.created_at && b.created_at) {
                        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                      }
                      return b.id - a.id;
                    })
                    .map(history => (
                      <Timeline.Item
                        key={history.id}
                        color={
                          history.status_code &&
                          history.status_code >= 200 &&
                          history.status_code < 300
                            ? 'green'
                            : history.status_code && history.status_code >= 400
                              ? 'red'
                              : 'gray'
                        }
                      >
                        <div
                          style={{
                            background: '#fafafa',
                            padding: '12px',
                            borderRadius: '6px',
                            border: '1px solid #e8e8e8',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              marginBottom: '8px',
                            }}
                          >
                            <Text strong style={{ fontSize: '14px' }}>
                              {history.action}
                            </Text>
                            {history.status_code && (
                              <Tag
                                color={
                                  history.status_code >= 200 && history.status_code < 300
                                    ? 'green'
                                    : history.status_code >= 400
                                      ? 'red'
                                      : 'default'
                                }
                                style={{ fontSize: '11px' }}
                              >
                                {history.status_code}
                              </Tag>
                            )}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              fontSize: '12px',
                              color: '#666',
                              marginBottom: '8px',
                            }}
                          >
                            <span>
                              <UserOutlined style={{ marginRight: '4px' }} />
                              {history.user_username || 'Unknown'}
                            </span>
                            {history.user_email && (
                              <span style={{ fontSize: '11px', color: '#999' }}>
                                ({history.user_email})
                              </span>
                            )}
                            <span>
                              <ClockCircleOutlined style={{ marginRight: '4px' }} />
                              {history.created_at
                                ? new Date(history.created_at).toLocaleString()
                                : `ID: ${history.id}`}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                            <Text type="secondary">
                              Object: {history.object_type_name} #{history.object_id}
                            </Text>
                            {history.object_title && (
                              <Text type="secondary" style={{ marginLeft: '8px' }}>
                                - {history.object_title}
                              </Text>
                            )}
                          </div>
                          {history.changed_fields && history.changed_fields.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              <Text
                                type="secondary"
                                style={{ fontSize: '11px', fontWeight: 'bold', display: 'block' }}
                              >
                                Changed Fields:
                              </Text>
                              <div style={{ marginTop: '4px' }}>
                                {history.changed_fields.map((field, idx) => {
                                  const prevVal = history.previous_data
                                    ? history.previous_data[field]
                                    : undefined;
                                  const newVal = history.new_data
                                    ? history.new_data[field]
                                    : undefined;
                                  const formatValue = (val: unknown): string => {
                                    if (val === null || val === undefined) return '-';
                                    if (typeof val === 'object') {
                                      try {
                                        return JSON.stringify(val);
                                      } catch {
                                        return String(val);
                                      }
                                    }
                                    return String(val);
                                  };
                                  const prevStr = formatValue(prevVal);
                                  const newStr = formatValue(newVal);
                                  return (
                                    <div
                                      key={idx}
                                      style={{
                                        marginBottom: '6px',
                                        padding: '6px 8px',
                                        background: '#f5f5f5',
                                        borderRadius: '4px',
                                        border: '1px solid #e8e8e8',
                                      }}
                                    >
                                      <Tag
                                        color="processing"
                                        style={{ fontSize: '11px', marginBottom: '4px' }}
                                      >
                                        {field}
                                      </Tag>
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'flex-start',
                                          gap: '8px',
                                          marginTop: '4px',
                                          fontSize: '11px',
                                        }}
                                      >
                                        <div
                                          style={{
                                            flex: 1,
                                            background: '#fff1f0',
                                            padding: '4px 8px',
                                            borderRadius: '3px',
                                            border: '1px solid #ffa39e',
                                            wordBreak: 'break-all',
                                          }}
                                        >
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: '10px', display: 'block' }}
                                          >
                                            Before
                                          </Text>
                                          <Text
                                            delete
                                            style={{
                                              fontSize: '11px',
                                              color: '#cf1322',
                                            }}
                                          >
                                            {prevStr}
                                          </Text>
                                        </div>
                                        <span style={{ lineHeight: '32px', color: '#999' }}>→</span>
                                        <div
                                          style={{
                                            flex: 1,
                                            background: '#f6ffed',
                                            padding: '4px 8px',
                                            borderRadius: '3px',
                                            border: '1px solid #b7eb8f',
                                            wordBreak: 'break-all',
                                          }}
                                        >
                                          <Text
                                            type="secondary"
                                            style={{ fontSize: '10px', display: 'block' }}
                                          >
                                            After
                                          </Text>
                                          <Text
                                            style={{
                                              fontSize: '11px',
                                              color: '#389e0d',
                                              fontWeight: 500,
                                            }}
                                          >
                                            {newStr}
                                          </Text>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </Timeline.Item>
                    ))}
                </Timeline>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <HistoryOutlined
                    style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }}
                  />
                  <div>
                    <Text type="secondary" strong style={{ fontSize: '16px' }}>
                      No action history available
                    </Text>
                  </div>
                  <Text type="secondary">No actions have been performed yet</Text>
                </div>
              )}
            </div>
          </Spin>
        </Modal>
      </div>
    </Component.BasePageComponent>
  );
};

export default EpisodeEdit;
